//
// Single-activity (non-multi-program) report preview.
// Ported from takso-poc/takso-reporting-ui/src/pages/reporting/ReportingPage.jsx
// (inline ReportPreview component + BackgroundTable + FastFactsTable).
//
// Uses the same shared building blocks as MultiProgramPreview — REPORT_FONT,
// CollapsibleSection, SubHeading, WriterNote, AuthorComment — so all report
// formats have a consistent visual family.
//
// Assist content gating via assistPrefs:
//   assistPrefs.showWriterNote — pre-filled example text inside WriterNote areas
//
// AuthorComment is still used for the one unconditional "no quotes shortlisted"
// data-state notice — it is not part of the assist-preference system. Every
// custom-insertion break in this file renders a single CustomSectionSlot
// (see CustomSectionSlot.jsx) — a single-line "Add custom section (optional)"
// button that, once used, can hold a heading, rich text, and/or images —
// replacing the older separate WriterNote/GraphicSlot pair per break.
//
// Props:
//   selectedActivities  — array of activity objects (from the activity list)
//   venues              — string | null — comma-sep venue names for primary activity
//   objectives          — array | null — [{ outcomeTitle, tocKnown, tocDo, evidence[] }]
//   crossRefs           — { heading, items } | null
//   fastFacts           — { expenditure, totalParticipants, fieldGroups, outputs } | null
//   evaluationPlans     — array | null — [{
//                           planId, planName,
//                           outcomes: [{ outcomeTitle, domain }]
//                         }]
//                         Each plan maps to one Evaluation Plan record in the Takso activity.
//                         Displayed in report order: participants → analysis → chart.
//   orgName             — string | null
//   orgId               — number | null
//   prefs               — document preferences from PreferencesTab
//   assistPrefs         — { enabled, showWriterNote }
//   onEdit              — () => void — back to filter panel
//   onClear             — () => void — clear and go back

import React, {
  Fragment,
  useMemo,
  useCallback,
  useState,
  useRef,
  useEffect,
  useContext,
} from 'react';

import { useTranslation } from 'react-i18next';

import axios from 'axios';

import {
  REPORT_FONT,
  REPORT_WEIGHT,
  CollapsibleSection,
  SubHeading,
  DataPanel,
  WriterNoteHtml,
  SyncedField,
  AuthorComment,
} from '../../components/reports/ReportTemplate';

import { ReportTintContext } from '../../components/reports/ReportTintContext';
import ImagePickerModal from '../../components/reports/ImagePickerModal';
import { HtmlService } from '../../services';
import { chartApiGet, reportBuilderGet } from '../../lib/chartApi';
import FinancialResultTable from './FinancialResultTable';
import ContractorMap from '../../components/charts/ContractorMap';
import { LocaleService } from '../../services';

import CoverImageCropModal from './CoverImageCropModal';
import CustomSectionSlot, { CustomSectionModal } from './CustomSectionSlot';

const STATUS = {
  Unapproved: { label: 'Development', hex: '#EE7A00' },
  Approved: { label: 'Active', hex: '#50972C' },
  Archived: { label: 'Completed', hex: '#1D3498' },
};

// Flattens the category → occupations tree returned by /charts/contractors
// into one row per specific occupation ("Service type"), each still carrying
// its parent category label — the count shown is per occupation, not the
// category total.
const flattenOccupationRows = (categories) =>
  (categories || []).flatMap((cat) =>
    (cat.occupations?.length
      ? cat.occupations
      : [{ occupationCode: null, label: cat.label, count: cat.count }]
    ).map((occ) => ({
      key: `${cat.groupCode ?? 'x'}-${occ.occupationCode ?? 'x'}`,
      count: occ.count,
      categoryLabel: cat.label,
      serviceType: occ.label,
    }))
  );

const dateRange = (s, e) => {
  const a = LocaleService.fmtLocaleDate(s);
  const b = LocaleService.fmtLocaleDate(e);
  if (!a && !b) return null;
  if (!b || a === b) return a;
  return `${a} – ${b}`;
};

// Inline style for grey-italic placeholder text
const PH = { fontStyle: 'italic', color: '#c0c0c0' };

// ── A4 constants ─────────────────────────────────────────────────────────────
const A4_W_PX = 794; // 210mm at 96 dpi
const A4_H_PX = 1123; // 297mm at 96 dpi

// ── Page break indicator ──────────────────────────────────────────────────────
// Renders dashed lines over the paper at positions that avoid orphaning
// headings or splitting short blocks mid-content.
//
// Algorithm: for each ideal A4-page boundary (every 1123px), scan all
// block-level elements in the content area. If an element would be cut by
// the ideal break line, move the line to just above that element's top edge
// (so the element starts on the next page). The snap range is ±120px — if no
// element is being cut within that band, the ideal position is kept.

const SNAP_RANGE = 180; // px either side of ideal break to search

// Separate selectors so the snap algorithm can treat headings/guards
// differently from plain paragraphs.
// GUARD: elements we always want to keep at the TOP of a page (headings, TOC)
const GUARD_SELECTOR = '[data-break-guard]';
// CONTENT: elements that should not be split mid-element (includes WriterNote via data-break-avoid)
const CONTENT_SELECTOR = 'p, li, blockquote, table, [data-break-avoid]';
// FORCED: elements that always begin on a new page (e.g. corporate statement)
const FORCED_SELECTOR = '[data-force-break]';
// KEEP: blocks that must never be split (e.g. the Financial Result table with
// its heading) — a break that would cut one moves to just before it, however
// far back that is, as long as the block fits on a page
const KEEP_SELECTOR = '[data-keep-together]';

const PageBreaks = ({ contentRef, onLinesChange }) => {
  const { t } = useTranslation();
  const [lines, setLines] = useState([]);

  useEffect(() => {
    const calc = () => {
      if (!contentRef.current) return;

      const container = contentRef.current;
      // The dashed lines use position:absolute relative to the rb-paper parent
      const paper = container.parentElement;
      const paperRect = paper.getBoundingClientRect();

      const h = container.getBoundingClientRect().height;
      const pageCount = Math.floor(h / A4_H_PX);
      if (pageCount === 0) {
        setLines([]);
        return;
      }

      const toBlocks = (selector) =>
        Array.from(container.querySelectorAll(selector))
          .map((el) => {
            const r = el.getBoundingClientRect();
            return {
              top: r.top - paperRect.top,
              bottom: r.bottom - paperRect.top,
              height: r.height,
            };
          })
          .filter((b) => b.height > 4);

      // Guards: headings / TOC — always try to keep at top of page
      const guards = toBlocks(GUARD_SELECTOR);
      // Content: paragraphs / tables / WriterNotes — only snap if being cut
      const content = toBlocks(CONTENT_SELECTOR);
      // Keep-together blocks — never split, regardless of snap range
      const keeps = toBlocks(KEEP_SELECTOR);

      // Forced breaks — elements that always start on a new page.
      // Collect their top positions and insert visual breaks just before them.
      // Also collect their full bounding rects so we can skip page boundaries
      // that fall inside them (e.g. a multi-page TOC must not be split).
      const forcedEls = Array.from(container.querySelectorAll(FORCED_SELECTOR));
      const forcedYs = forcedEls
        .map((el) => el.getBoundingClientRect().top - paperRect.top - 4)
        .filter((y) => y > 0);
      const forcedBlocks = forcedEls.map((el) => {
        const r = el.getBoundingClientRect();
        return {
          top: r.top - paperRect.top - 8,
          bottom: r.bottom - paperRect.top + 4,
        };
      });

      const newLines = [...forcedYs];

      // Finds the nearest guard heading whose bottom sits just above `y` —
      // that heading would be orphaned from the content below it. Takes `y`
      // as a parameter (rather than closing over avoidOrphan's loop-scoped
      // variable) so it isn't "a function declared in a loop" from ESLint's
      // point of view — it's called synchronously each pass either way, so
      // there's no behaviour difference, just a cleaner closure.
      const findOrphanedGuard = (y) =>
        guards
          .filter((b) => b.bottom <= y + 4 && b.bottom >= y - 100 && b.top < y)
          .sort((a, b) => b.top - a.top)[0];

      // After any snap decision, check whether a guard heading's bottom sits just
      // above the chosen break line — that heading would be orphaned from the content
      // below. If found, pull the break up to just before that heading instead.
      // Repeats up to two times to handle stacked headings (e.g. H1 then H3).
      const avoidOrphan = (snapY) => {
        let y = snapY;
        for (let pass = 0; pass < 2; pass++) {
          const orphaned = findOrphanedGuard(y);
          if (!orphaned) break;
          y = orphaned.top - 4;
        }
        return y;
      };

      for (let i = 1; i <= pageCount; i++) {
        const idealY = A4_H_PX * i;

        // Skip this ideal break if a forced break already covers this page boundary
        // (within 40% of a page height — avoids double-breaks)
        if (newLines.some((f) => Math.abs(f - idealY) < A4_H_PX * 0.4))
          continue;

        // Skip breaks that fall inside a forced-break block (e.g. a tall TOC).
        if (forcedBlocks.some((b) => idealY > b.top && idealY < b.bottom))
          continue;

        // 0. Is a keep-together block being cut? Break just before it.
        const keepCut = keeps.find(
          (b) => b.top < idealY && b.bottom > idealY && b.height < A4_H_PX * 0.9
        );
        if (keepCut) {
          newLines.push(avoidOrphan(keepCut.top - 4));
          continue;
        }

        // 1. Is any content element being CUT by this break?
        const cut = content.find(
          (b) => b.top < idealY && b.bottom > idealY && b.height < A4_H_PX * 0.6
        );
        if (cut) {
          newLines.push(
            avoidOrphan(Math.max(cut.top - 4, idealY - SNAP_RANGE))
          );
          continue;
        }

        // 2. Is a guard element starting just after the break (within snap range)?
        //    Move the break to just before it so it opens the new page.
        const guardAfter = guards
          .filter((b) => b.top > idealY - 8 && b.top < idealY + SNAP_RANGE)
          .sort((a, b) => a.top - b.top)[0];
        if (guardAfter) {
          newLines.push(
            avoidOrphan(Math.max(guardAfter.top - 4, idealY - SNAP_RANGE))
          );
          continue;
        }

        // 3. Is a guard element ending just before the break (within snap range)?
        //    Move the break to just before it so the heading starts the new page.
        const guardBefore = guards
          .filter((b) => b.top < idealY && b.top > idealY - SNAP_RANGE)
          .sort((a, b) => b.top - a.top)[0]; // nearest before break
        if (guardBefore) {
          newLines.push(
            avoidOrphan(Math.max(guardBefore.top - 4, idealY - SNAP_RANGE))
          );
          continue;
        }

        newLines.push(avoidOrphan(idealY));
      }

      // Finds the topmost guard that ends just before `snapY` with nothing
      // (guard or content) between its bottom and the snap — i.e. stranded.
      // Same reasoning as findOrphanedGuard above: takes snapY as a
      // parameter instead of closing over the loop's reassigned variable.
      const findStrandedGuard = (snapY) =>
        guards
          .filter((b) => b.top < snapY && b.bottom < snapY)
          .filter(
            (b) =>
              ![...guards, ...content].some(
                (x) => x.top >= b.bottom + 4 && x.top < snapY - 4
              )
          )
          .sort((a, b) => b.top - a.top)[0];

      // Final sweep: catch "estranged parent" headings — a guard heading that sits
      // alone just above a break with no content between it and the break.
      // avoidOrphan only looks 100 px back; this pass catches headings that land
      // further above the snap yet still have no body content after them on that page.
      // Only run on computed ideal breaks, not forced breaks (forced positions must not move).
      const forcedSet = new Set(forcedYs.map((y) => Math.round(y)));
      for (let j = 0; j < newLines.length; j++) {
        if (forcedSet.has(Math.round(newLines[j]))) continue;
        let snapY = newLines[j];
        for (let pass = 0; pass < 2; pass++) {
          const strandedGuard = findStrandedGuard(snapY);
          if (!strandedGuard) break;
          newLines[j] = Math.max(strandedGuard.top - 4, snapY - SNAP_RANGE * 2);
          snapY = newLines[j];
        }
      }

      newLines.sort((a, b) => a - b);
      setLines(newLines);
      onLinesChange?.(newLines);
    };

    calc();
    // Debounced: ResizeObserver fires on every height change, including one
    // caused by pressing Enter inside a WriterNote field. Running calc()
    // synchronously on that resize — which calls setLines/onLinesChange and
    // triggers a full ReportPreview re-render — lands in the same tick as
    // the browser's native paragraph-split. That appears to be enough to
    // knock the caret back to its pre-split position: the DOM mutation
    // itself succeeds (a new empty paragraph is created) but the selection
    // doesn't move into it, so typing continues in the old paragraph and
    // Enter looks like it did nothing. Deferring calc() until 250ms after
    // the last resize lets the browser finish settling the caret first.
    let debounceTimer = null;
    const debouncedCalc = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(calc, 250);
    };
    const obs = new ResizeObserver(debouncedCalc);
    if (contentRef.current) obs.observe(contentRef.current);
    return () => {
      clearTimeout(debounceTimer);
      obs.disconnect();
    };
    // onLinesChange is always the setPageLines state setter (see call site),
    // which React guarantees is referentially stable — safe to include
    // without risking an extra effect run.
  }, [contentRef, onLinesChange]);

  if (lines.length === 0) return null;
  return (
    <>
      {lines.map((y, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: y,
            borderTop: '2px dashed #cbd5e1',
            zIndex: 10,
            pointerEvents: 'none',
            textAlign: 'center',
          }}
        >
          <span
            style={{
              fontSize: 10,
              color: '#94a3b8',
              background: '#fff',
              padding: '0 10px',
              position: 'relative',
              top: -7,
              userSelect: 'none',
            }}
          >
            — {t('report.builder.preview.page_break')} —
          </span>
        </div>
      ))}
    </>
  );
};

// ── Record badge ─────────────────────────────────────────────────────────────
// Placed in the left margin of any field whose edits save back to the Takso
// activity record. The ↺ (sync) icon signals "this updates the record".
// Parent element must have position:relative.

const RecordBadge = () => {
  const { t } = useTranslation();

  return;
  <span
    title={t('report.builder.preview.edits_here_update_the_activity_record')}
    style={{
      position: 'absolute',
      left: -26,
      top: '50%',
      transform: 'translateY(-50%)',
      fontSize: 18,
      fontWeight: 700,
      color: '#ef8123',
      cursor: 'default',
      userSelect: 'none',
      lineHeight: 1,
    }}
  >
    ↺
  </span>;
};

// ── Quote picker ─────────────────────────────────────────────────────────────
// Renders a dropdown of shortlisted comments and displays selected ones as
// blockquotes. Two modes:
//   quotes = all shortlisted    → use under Findings (cross-outcome)
//   quotes = by-outcome subset  → use under "In the words of participants"
//
// selectedIds / onToggle manage the selection from the parent.

const TRUNC = 80;
const trunc = (s) => (s && s.length > TRUNC ? s.substring(0, TRUNC) + '…' : s);

const QuotePicker = ({
  quotes,
  selectedIds,
  onToggle,
  label,
  showTint,
  variant = 'bold',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const hasQuotes = quotes && quotes.length > 0;
  const selected = hasQuotes
    ? quotes.filter((q) => selectedIds.includes(q.id))
    : [];

  return (
    <div style={{ marginBottom: 8 }}>
      {/* Selected quotes */}
      {variant === 'bullet'
        ? /* Bullet list — Normal weight, no italic (evaluation plan sections) */
          selected.length > 0 && (
            <ul
              style={{
                margin: '4px 0',
                padding: showTint ? '4px 4px 4px 28px' : '0 0 0 20px',
                background: showTint ? '#f0fdf4' : 'transparent',
                borderRadius: showTint ? 4 : 0,
              }}
            >
              {selected.map((q) => (
                <li
                  key={q.id}
                  style={{
                    fontSize: REPORT_FONT.body,
                    fontWeight: REPORT_WEIGHT.body,
                    color: showTint ? '#166534' : '#111827',
                    lineHeight: 1.6,
                    marginBottom: 2,
                  }}
                >
                  {q.reason}
                </li>
              ))}
            </ul>
          )
        : /* Bold blockquote — Findings section */
          selected.map((q) => (
            <blockquote
              key={q.id}
              style={{
                margin: '4px 0',
                padding: showTint ? '4px 12px' : '0',
                borderLeft: showTint ? '3px solid #d1fae5' : 'none',
                background: showTint ? '#f0fdf4' : 'transparent',
                fontSize: REPORT_FONT.body,
                fontWeight: 700,
                color: '#111827',
                lineHeight: 1.6,
              }}
            >
              &ldquo;{q.reason}&rdquo;
            </blockquote>
          ))}

      {/* No quotes — full-width AuthorComment advisory */}
      {!hasQuotes && (
        <AuthorComment>
          [{t('report.builder.preview.no_participant_quotes')}]
        </AuthorComment>
      )}

      {/* Dropdown toggle — only shown when quotes exist */}
      {hasQuotes && (
        <div style={{ marginTop: 6, position: 'relative' }}>
          <button
            onClick={() => setOpen((v) => !v)}
            style={{
              fontSize: 14,
              color: '#6b7280',
              background: 'none',
              border: '1px dashed #d1d5db',
              borderRadius: 4,
              padding: '3px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span>
              {label || t('report.builder.preview.select_participant_quotes')}
            </span>
            <span>{open ? '▲' : '▼'}</span>
          </button>

          {open && hasQuotes && (
            <div
              onMouseDown={(e) => e.preventDefault()}
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                zIndex: 100,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                minWidth: 360,
                maxWidth: 520,
                maxHeight: 260,
                overflowY: 'auto',
                padding: '4px 0',
              }}
            >
              {quotes.map((q) => {
                const checked = selectedIds.includes(q.id);
                return (
                  <label
                    key={q.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      padding: '6px 12px',
                      cursor: 'pointer',
                      background: 'transparent',
                      borderBottom: '1px solid #f3f4f6',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        e.stopPropagation();
                        onToggle(q.id);
                      }}
                      style={{ marginTop: 3, flexShrink: 0 }}
                    />
                    <span style={{ lineHeight: 1.5 }}>
                      <span
                        style={{
                          fontWeight: 400,
                          color: '#111827',
                          display: 'block',
                          fontSize: 14,
                        }}
                      >
                        {trunc(q.reason)}
                      </span>
                      {variant !== 'bullet' && (
                        <span
                          style={{
                            fontWeight: 400,
                            color: '#166534',
                            display: 'block',
                            fontSize: 12,
                            marginTop: 2,
                          }}
                        >
                          {q.outcomeTitle}
                          {q.planName ? ` · ${q.planName}` : ''}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Background table ─────────────────────────────────────────────────────────

const BackgroundTable = ({ activity, venues }) => {
  const { t } = useTranslation();
  const when = dateRange(activity?.startDate, activity?.endDate);
  const rows = [
    {
      label: t('report.builder.preview.background_table.column_headings.title'),
      value: activity?.name,
      ph: '—',
    },
    activity?.teamName
      ? {
          label: t(
            'report.builder.preview.background_table.column_headings.team'
          ),
          value: activity?.teamName,
          ph: '—',
        }
      : null,
    when
      ? {
          label: t(
            'report.builder.preview.background_table.column_headings.when'
          ),
          value: when,
          ph: '—',
        }
      : null,
    venues
      ? {
          label: t(
            'report.builder.preview.background_table.column_headings.where'
          ),
          value: venues,
          ph: '—',
        }
      : null,
    activity?.outputType
      ? {
          label: t(
            'report.builder.preview.background_table.column_headings.type'
          ),
          value: activity?.outputType,
          ph: '—',
        }
      : null,
  ].filter(Boolean);

  return (
    <table className="rb-bg-table" style={{ marginTop: 6, marginBottom: 10 }}>
      <tbody>
        {rows.map(({ label, value, ph }) => (
          <tr key={label}>
            <td>{label}</td>
            <td style={value ? {} : { color: '#9ca3af', fontStyle: 'italic' }}>
              {value || ph}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ── Fast facts table ─────────────────────────────────────────────────────────

const fmtCurrency = (n) =>
  '$' +
  Number(n).toLocaleString('en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

// Returns white or black depending on hex background luminance
const contrastColour = (hex = '#ffffff') => {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b < 128 ? '#ffffff' : '#000000';
};

const FastFactsTable = ({
  fastFacts,
  headerBg = '#ffffff',
  showTint = true,
}) => {
  const { t } = useTranslation();
  const rows = fastFacts?.rows || [];
  const headerText = contrastColour(headerBg);

  if (!rows.length) {
    return (
      <div
        style={{
          marginTop: 4,
          fontSize: REPORT_FONT.note,
          fontStyle: 'italic',
          color: '#9ca3af',
        }}
      >
        [{t('report.builder.preview.no_fast_facts_available')}]
      </div>
    );
  }

  return (
    <table className="rb-ff-table">
      <thead>
        <tr>
          <th
            colSpan={2}
            style={{ background: headerBg, color: headerText, fontWeight: 700 }}
          >
            {t('report.builder.preview.inputs_and_outputs')}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ label, value, type }) => (
          <tr key={label}>
            <td>
              {value != null
                ? type === 'currency'
                  ? fmtCurrency(value)
                  : String(value)
                : '–'}
            </td>
            <td>{label}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ── Theory-of-change line helper ─────────────────────────────────────────────

// TocLine — one sentence of the Theory of Change.
// Label = template text (Category 1, amber tint when toggle on).
// Body  = locked activity-record data (Category 2, green tint when toggle on).
const TocLine = ({ label, body, ph }) => {
  const showTint = useContext(ReportTintContext);
  return (
    <p
      style={{
        fontSize: REPORT_FONT.body,
        fontFamily: 'Arial, sans-serif',
        margin: '0 0 4px',
        lineHeight: 1.5,
        color: '#111827',
      }}
    >
      {/* Template prefix — amber tint */}
      <span
        style={{
          fontWeight: 700,
          background: showTint ? '#fff9e3' : 'transparent',
          color: '#111827',
          borderRadius: showTint ? 2 : 0,
          padding: showTint ? '1px 3px' : 0,
        }}
      >
        {label}
      </span>
      {/* Body — non-editable DB value (green) or placeholder */}
      {body ? (
        <span
          style={{
            background: showTint ? '#f0fdf4' : 'transparent',
            color: showTint ? '#166534' : '#111827',
            borderRadius: showTint ? 2 : 0,
            padding: showTint ? '1px 3px' : 0,
          }}
        >
          {body}
        </span>
      ) : (
        <em style={{ color: '#9ca3af' }}>{ph}</em>
      )}
    </p>
  );
};

// ── Level-3 heading (Theory of change / Evidence / In the words of…) ─────────

// L3 heading — template structure text (Category 1, amber tint when toggle on).
// data-break-guard ensures the page-break snap logic keeps it with content below.
const L3Heading = ({ children }) => {
  const showTint = useContext(ReportTintContext);
  return (
    <div
      data-break-guard="h3"
      style={{
        fontSize: REPORT_FONT.h3,
        fontWeight: REPORT_WEIGHT.h3,
        color: '#111827',
        marginTop: 12,
        marginBottom: 4,
        background: showTint ? '#fff9e3' : 'transparent',
        borderRadius: showTint ? 3 : 0,
        padding: showTint ? '2px 6px' : 0,
        breakAfter: 'avoid',
        pageBreakAfter: 'avoid',
      }}
    >
      {children}
    </div>
  );
};

// ── Derive record type / manager type ────────────────────────────────────────

const activityContext = (t, activity) => {
  if (!activity) return { recordType: null, managerType: null };
  let recordType;
  if (activity.isProgram) {
    recordType = t('report.builder.preview.activity_record_type.program');
  } else if (activity.parentId) {
    recordType = t('report.builder.preview.activity_record_type.single_linked');
  } else {
    recordType = t(
      'report.builder.preview.activity_record_type.single_independent'
    );
  }
  const managerType = activity.isGuestManager
    ? t('report.builder.preview.manager_types.guest')
    : t('report.builder.preview.manager_types.staff');
  return { recordType, managerType };
};

// ── Main component ───────────────────────────────────────────────────────────

const ReportPreview = ({
  selectedActivities,
  venues,
  objectives,
  crossRefs,
  fastFacts,
  evaluationPlans = null,
  shortlistedComments = { all: [], byOutcome: {} },
  surveyResponseComments = { hasComments: false, sessions: [] },
  orgName,
  orgId,
  prefs = {},
  chartImages = {},
  onEdit,
  onClear,
  assistPrefs = {},
}) => {
  // ── Report customisation — persisted to sReportCustomisation via API ─────────
  // Holds all WriterNote text, image selections, and quote selections that are
  // not written back to other DB fields. Loaded once per activity on mount.
  const { t } = useTranslation();
  const primaryActivityId = selectedActivities?.[0]?.id ?? null;
  const [customisation, setCustomisation] = useState({});
  const customisationRef = useRef({});

  // ── Contractors appendix — occupation-category counts ────────────────────
  // Reuses the existing Report Builder /charts/contractors endpoint
  // (getInputsContractors), scoped to this one activity.
  const [contractorCategories, setContractorCategories] = useState([]);
  useEffect(() => {
    if (!primaryActivityId) return;
    chartApiGet('contractors', { activityIds: primaryActivityId })
      .then((d) => setContractorCategories(d?.categories || []))
      .catch(() => setContractorCategories([]));
  }, [primaryActivityId]);
  const totalContractors = contractorCategories.reduce(
    (s, c) => s + (c.count || 0),
    0
  );

  // ── Appendix 2: Financial Result (TAK-2086) ──────────────────────────────────
  // The same figures as the Documentation page's Financial Summary card.
  // Re-fetched when the tab regains focus, since the amounts are edited on the
  // activity page while the report is open alongside it.
  const [financialSummary, setFinancialSummary] = useState(null);
  useEffect(() => {
    if (!primaryActivityId) {
      setFinancialSummary(null);
      return undefined;
    }
    let cancelled = false;
    const load = () =>
      reportBuilderGet('activity-financials', { activityId: primaryActivityId })
        .then((j) => {
          if (!cancelled) {
            setFinancialSummary(j.ok ? j.financials || null : null);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setFinancialSummary(null);
          }
        });
    load();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        load();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [primaryActivityId]);

  // ── Executive Summary cross-tab sync via BroadcastChannel ───────────────────
  // desc* tracks the final activity description (sProject.summary).
  // analysis* tracks the Findings text (sProject.analysis).
  // Override states hold locally-confirmed saves or inbound broadcasts from the
  // activity UI. Version bumps force WriterNote remounts with fresh content.
  const [descOverride, setDescOverride] = useState(null);
  const [descVersion, setDescVersion] = useState(0);
  const [analysisOverride, setAnalysisOverride] = useState(null);
  const [analysisVersion, setAnalysisVersion] = useState(0);
  const [nextStepsOverride, setNextStepsOverride] = useState(null);
  const [nextStepsVersion, setNextStepsVersion] = useState(0);
  const [resultOverride, setResultOverride] = useState(null);
  const [resultVersion, setResultVersion] = useState(0);
  const [financialNoteOverride, setFinancialNoteOverride] = useState(null);
  const [financialNoteVersion, setFinancialNoteVersion] = useState(0);
  const [reflectionOverride, setReflectionOverride] = useState(null);
  const [reflectionVersion, setReflectionVersion] = useState(0);
  const [methodologyOverride, setMethodologyOverride] = useState(null);
  const [methodologyVersion, setMethodologyVersion] = useState(0);
  const [reportInclusionsOverride, setReportInclusionsOverride] =
    useState(null);
  // Per-outcome evaluation narrative (sEvaluation.reportNarrative) — keyed by
  // evaluationId rather than a single override/version pair, since one
  // activity can have several evaluation plans each with several outcomes,
  // each with its own independent narrative.
  const [narrativeOverrides, setNarrativeOverrides] = useState({});

  useEffect(() => {
    setDescOverride(null);
    setDescVersion(0);
    setAnalysisOverride(null);
    setAnalysisVersion(0);
    setNextStepsOverride(null);
    setNextStepsVersion(0);
    setResultOverride(null);
    setResultVersion(0);
    setFinancialNoteOverride(null);
    setFinancialNoteVersion(0);
    setReflectionOverride(null);
    setReflectionVersion(0);
    setMethodologyOverride(null);
    setMethodologyVersion(0);
    setReportInclusionsOverride(null);
    setNarrativeOverrides({});
  }, [primaryActivityId]);

  useEffect(() => {
    if (!primaryActivityId) return;
    const ch = new BroadcastChannel('takso_activity');
    ch.onmessage = (e) => {
      if (e.data?.activityId !== primaryActivityId) return;
      if (e.data.type === 'summary') {
        setDescOverride(e.data.summary);
        setDescVersion((v) => v + 1);
      } else if (e.data.type === 'analysis') {
        setAnalysisOverride(e.data.analysis);
        setAnalysisVersion((v) => v + 1);
      } else if (e.data.type === 'nextSteps') {
        setNextStepsOverride(e.data.nextSteps);
        setNextStepsVersion((v) => v + 1);
      } else if (e.data.type === 'result') {
        setResultOverride(e.data.result);
        setResultVersion((v) => v + 1);
      } else if (e.data.type === 'reflection') {
        setReflectionOverride(e.data.reflection);
        setReflectionVersion((v) => v + 1);
      } else if (e.data.type === 'methodology') {
        setMethodologyOverride(e.data.evaluationMethodology);
        setMethodologyVersion((v) => v + 1);
      } else if (e.data.type === 'financialNote') {
        setFinancialNoteOverride(e.data.financialNote);
        setFinancialNoteVersion((v) => v + 1);
      } else if (e.data.type === 'reportInclusions') {
        setReportInclusionsOverride(e.data.reportInclusions);
      } else if (e.data.type === 'evaluationNarrative') {
        setNarrativeOverrides((prev) => {
          const existing = prev[e.data.evaluationId];
          return {
            ...prev,
            [e.data.evaluationId]: {
              text: e.data.reportNarrative,
              version: (existing?.version ?? 0) + 1,
            },
          };
        });
      }
    };
    return () => ch.close();
  }, [primaryActivityId]);

  useEffect(() => {
    if (!primaryActivityId) return;
    let cancelled = false;
    axios
      .get(`/project/${primaryActivityId}/report-customisation`)
      .then((res) => {
        if (cancelled) return;
        const c = res.data?.customisation || {};
        customisationRef.current = c;
        setCustomisation(c);
      })
      .catch((e) => console.error('Failed to load report customisation', e));
    return () => {
      cancelled = true;
    };
  }, [primaryActivityId]);

  // Deep-merges a patch into the customisation state and saves to the DB.
  // patch shape mirrors the sReportCustomisation JSON — e.g.:
  //   { customSections: { 'exec-background': { has, heading, text, images } } }
  //   { quoteSelections: { findings: [1,2,3] } }
  const patchCustomisation = useCallback(
    async (patch) => {
      if (!primaryActivityId) return;
      try {
        const res = await axios.patch(
          `/project/${primaryActivityId}/report-customisation`,
          patch
        );
        const next = res.data?.customisation || {};
        customisationRef.current = next;
        setCustomisation(next);
      } catch (e) {
        console.error('Failed to save report customisation', e);
      }
    },
    [primaryActivityId]
  );

  // ── Save narrative — fires from the Evaluation Analysis WriterNote on blur ───
  // Updates sevaluation.reportNarrative and keeps the Takso activity UI in sync.
  const saveNarrative = useCallback(
    async (evaluationId, text) => {
      if (!primaryActivityId || !evaluationId) return;
      try {
        await axios.put(
          `/project/${primaryActivityId}/evaluation/${evaluationId}/narrative`,
          { reportNarrative: text }
        );
        setNarrativeOverrides((prev) => {
          const existing = prev[evaluationId];
          return {
            ...prev,
            [evaluationId]: { text, version: (existing?.version ?? 0) + 1 },
          };
        });
        const ch = new BroadcastChannel('takso_activity');
        ch.postMessage({
          type: 'evaluationNarrative',
          activityId: primaryActivityId,
          evaluationId,
          reportNarrative: text,
        });
        ch.close();
      } catch (e) {
        console.error('Failed to save evaluation narrative', e);
      }
    },
    [primaryActivityId]
  );

  // ── Assist gates ─────────────────────────────────────────────────────────────
  // Each falls back to assistPrefs.enabled when the sub-key is not explicitly set.
  const assistEnabled = Boolean(assistPrefs.enabled);
  const showWriterNote =
    assistPrefs.showWriterNote !== undefined
      ? assistPrefs.showWriterNote
      : assistEnabled;

  // ── Quote selections — seeded from customisation once it loads ───────────────
  const [findingsQuoteIds, setFindingsQuoteIds] = useState([]);
  const [participantQuoteIds, setParticipantQuoteIds] = useState({});

  // Seed quote state when customisation loads (or activity changes).
  useEffect(() => {
    const qs = customisation.quoteSelections || {};
    setFindingsQuoteIds(qs.findings || []);
    setParticipantQuoteIds(qs.evaluations || {});
  }, [customisation]);

  const toggleFindingsQuote = (id) => {
    setFindingsQuoteIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id];
      // Send full quoteSelections so the merge doesn't clobber evaluations.
      const currentEvaluations =
        customisationRef.current.quoteSelections?.evaluations || {};
      patchCustomisation({
        quoteSelections: { findings: next, evaluations: currentEvaluations },
      });
      return next;
    });
  };

  const toggleParticipantQuote = (evaluationId, responseId) => {
    setParticipantQuoteIds((prev) => {
      const current = prev[evaluationId] || [];
      const next = {
        ...prev,
        [evaluationId]: current.includes(responseId)
          ? current.filter((x) => x !== responseId)
          : [...current, responseId],
      };
      // Send full quoteSelections so the merge doesn't clobber findings.
      const currentFindings =
        customisationRef.current.quoteSelections?.findings || [];
      patchCustomisation({
        quoteSelections: { findings: currentFindings, evaluations: next },
      });
      return next;
    });
  };

  // ── Paper ref for page break calculation ─────────────────────────────────────
  const paperContentRef = useRef(null);
  // Computed break positions (in px from paper top) — updated by PageBreaks after each layout.
  const [pageLines, setPageLines] = useState([]);

  // ── WYSIWYG tint toggle ──────────────────────────────────────────────────────
  // When off, blue data-table backgrounds are hidden for a print-like view.
  const [showDataTint, setShowDataTint] = useState(true);

  // ── Image picker — shared modal for cover and custom-section graphics ───────
  const [showImagePicker, setShowImagePicker] = useState(false);
  // null = cover page slot; any string = a custom-section slot key
  const [imagePickerSlot, setImagePickerSlot] = useState(null);
  const [imagePickerMultiple, setImagePickerMultiple] = useState(false);

  const openImagePicker = useCallback(
    (slot = null, { multiple = false } = {}) => {
      setImagePickerSlot(slot);
      setImagePickerMultiple(multiple);
      setShowImagePicker(true);
    },
    []
  );

  // ── Custom sections — replaces the old per-slot WriterNote+GraphicSlot pair.
  // customisation.customSections[slot] = {
  //   has: { heading, text, images },   // which pieces this slot has added
  //   heading, text,                    // heading plain text / text rich HTML
  //   images: [{ documentId, url, name }],
  // }
  // Always writes the FULL per-slot object back — patchCustomisation only
  // deep-merges one level, so a patch of just `{ heading }` would silently
  // wipe out that slot's `text`/`images` siblings on the backend.
  const [customSectionModalSlot, setCustomSectionModalSlot] = useState(null);

  const openCustomSectionModal = useCallback(
    (slot) => setCustomSectionModalSlot(slot),
    []
  );

  const handleAddCustomSection = useCallback(
    (opts) => {
      const slot = customSectionModalSlot;
      setCustomSectionModalSlot(null);
      if (!slot) return;
      const existing = customisationRef.current.customSections?.[slot];
      const next = {
        has: {
          heading: !!(existing?.has?.heading || opts.heading),
          text: !!(existing?.has?.text || opts.text),
          images: !!(existing?.has?.images || opts.images),
        },
        heading: existing?.heading || '',
        text: existing?.text || '',
        images: existing?.images || [],
      };
      patchCustomisation({ customSections: { [slot]: next } });
      if (opts.images) {
        openImagePicker(slot, { multiple: true });
      }
    },
    [customSectionModalSlot, patchCustomisation, openImagePicker]
  );

  const saveCustomSectionField = useCallback(
    (slot, field, value) => {
      const existing = customisationRef.current.customSections?.[slot];
      if (!existing) return;
      patchCustomisation({
        customSections: { [slot]: { ...existing, [field]: value } },
      });
    },
    [patchCustomisation]
  );

  const addCustomSectionImages = useCallback(
    (slot, imgs) => {
      const existing = customisationRef.current.customSections?.[slot];
      if (!existing) return;
      patchCustomisation({
        customSections: {
          [slot]: {
            ...existing,
            images: [
              ...(existing.images || []),
              ...imgs.map((img) => ({
                documentId: img.documentId,
                url: img.url,
                name: img.name,
              })),
            ],
          },
        },
      });
    },
    [patchCustomisation]
  );

  const removeCustomSectionImage = useCallback(
    (slot, index) => {
      const existing = customisationRef.current.customSections?.[slot];
      if (!existing) return;
      patchCustomisation({
        customSections: {
          [slot]: {
            ...existing,
            images: (existing.images || []).filter((_, i) => i !== index),
          },
        },
      });
    },
    [patchCustomisation]
  );

  const removeCustomSection = useCallback(
    (slot) => patchCustomisation({ customSections: { [slot]: null } }),
    [patchCustomisation]
  );

  // Bundles the common prop set for a <CustomSectionSlot> at a given break.
  const customSectionProps = useCallback(
    (slot) => ({
      slot,
      data: customisation.customSections?.[slot],
      anchorId: `custom-${slot}`,
      onOpenModal: () => openCustomSectionModal(slot),
      onHeadingBlur: (text) => saveCustomSectionField(slot, 'heading', text),
      onTextBlur: (html) => saveCustomSectionField(slot, 'text', html),
      onOpenImagePicker: () => openImagePicker(slot, { multiple: true }),
      onRemoveImage: (idx) => removeCustomSectionImage(slot, idx),
      onRemoveSection: () => removeCustomSection(slot),
    }),
    [
      customisation.customSections,
      openCustomSectionModal,
      saveCustomSectionField,
      openImagePicker,
      removeCustomSectionImage,
      removeCustomSection,
    ]
  );
  // coverImg: { documentId, url, image: {xPct,yPct,widthPct,heightPct},
  //             crop: {xPct,yPct,widthPct,heightPct} } | null
  // image and crop are both % of the PAGE's own width/height, and fully
  // independent of each other — resizing/moving one never rescales or
  // distorts the other. image is the photo's own pan/zoom; crop is the crop
  // line, loaded tracing the photo's own edges (see CoverImageCropModal).
  // An earlier version made the photo's rect a percentage of a resizable
  // region box, which visibly stretched/distorted the photo whenever that
  // box was resized non-uniformly — keeping both in the same independent,
  // page-relative coordinate space is what avoids that: cropping only ever
  // changes how much of the (otherwise untouched) photo is visible. Both
  // can go negative or exceed 100 (photo may pan/zoom past the crop line;
  // crop line may extend past the page's edges) — only the overlapping part
  // ever shows, via the nested clip math below. Percentages (rather than
  // fixed pixels, or the old CSS background-position/size approach) mean
  // the same relative position reproduces correctly regardless of the
  // frame's actual rendered size, and can't silently invert direction the
  // way background-position math did for images whose auto-scaled height
  // ended up shorter than the frame.
  // Per-activity — loaded from sReportCustomisation (customisation.coverImage),
  // same blob/effect as customSections, so it resets correctly when the
  // selected activity changes instead of leaking between activities.
  const [coverImg, setCoverImg] = useState(
    () => customisation.coverImage || null
  );
  // Reset immediately on activity switch — don't wait for the customisation
  // fetch to resolve. Without this, the previous activity's cover image stays
  // on screen (and is briefly editable/removable) until the new fetch lands,
  // which reads as the wrong activity's photo "leaking" into the new report.
  useEffect(() => {
    setCoverImg(null);
  }, [primaryActivityId]);
  // Sync coverImg when customisation (re)loads — on mount and on activity switch.
  useEffect(() => {
    setCoverImg(customisation.coverImage || null);
  }, [customisation]);

  const saveCoverImage = useCallback(
    (update) => patchCustomisation({ coverImage: update }),
    [patchCustomisation]
  );

  // Crop modal — open with either a freshly-picked image (pendingCoverImage
  // set, no prior crop) or the existing cover image (reposition/re-crop).
  const [pendingCoverImage, setPendingCoverImage] = useState(null); // { documentId, url } | null
  const [showCoverCropModal, setShowCoverCropModal] = useState(false);

  // When user confirms image picker selection — routes to cover or a
  // custom-section slot (single image for cover; one or more for a slot).
  const handleImagePickerSelect = useCallback(
    (imgOrImgs) => {
      setShowImagePicker(false);
      if (!imagePickerSlot) {
        // Cover page — open the crop modal instead of saving directly.
        setPendingCoverImage({
          documentId: imgOrImgs.documentId,
          url: imgOrImgs.url,
          description: imgOrImgs.description || null,
          imageCredit: imgOrImgs.imageCredit || null,
        });
        setShowCoverCropModal(true);
      } else {
        addCustomSectionImages(
          imagePickerSlot,
          Array.isArray(imgOrImgs) ? imgOrImgs : [imgOrImgs]
        );
      }
      setImagePickerSlot(null);
      setImagePickerMultiple(false);
    },
    [imagePickerSlot, addCustomSectionImages]
  );

  const handleCoverCropSave = useCallback(
    ({ image, crop }) => {
      const source = pendingCoverImage || coverImg;
      const next = {
        documentId: source.documentId,
        url: source.url,
        description: source.description || null,
        imageCredit: source.imageCredit || null,
        image,
        crop,
      };
      setCoverImg(next);
      saveCoverImage(next);
      setShowCoverCropModal(false);
      setPendingCoverImage(null);
    },
    [pendingCoverImage, coverImg, saveCoverImage]
  );

  const handleCoverCropCancel = useCallback(() => {
    setShowCoverCropModal(false);
    setPendingCoverImage(null);
  }, []);

  // Memoized so their object identity only changes when the underlying saved
  // values actually change (after a save), not on every unrelated re-render
  // of this component. CoverImageCropModal's reset-on-open effect depends on
  // these references — inline object literals here would recreate "new"
  // props on every render, re-firing that effect mid-drag and silently
  // snapping the user's in-progress resize/reposition back to the last save.
  const coverCropInitialImage = useMemo(() => {
    if (pendingCoverImage || coverImg?.image == null) return undefined;
    return { ...coverImg.image };
  }, [pendingCoverImage, coverImg?.image]);
  const coverCropInitialCrop = useMemo(() => {
    if (pendingCoverImage || coverImg?.crop == null) return undefined;
    return { ...coverImg.crop };
  }, [pendingCoverImage, coverImg?.crop]);

  // ── Activity metadata ────────────────────────────────────────────────────────
  const names = selectedActivities.map((a) => a.name);
  const primary = selectedActivities[0] || null;

  // Include-in-report state — written by the activity UI, read here.
  // Override arrives via BroadcastChannel when the activity UI saves a toggle.
  // Default true (included) for all fields when absent.
  const reportInclusions =
    reportInclusionsOverride ??
    (() => {
      try {
        return JSON.parse(primary?.reportInclusions || '{}');
      } catch {
        return {};
      }
    })();

  // ── Appendix numbering ────────────────────────────────────────────────────
  // Evidence is always Appendix 1. Financial Result (single activities, when
  // "Show in report" is on), Survey responses and Contractors are only
  // numbered (and only appear at all) when they apply — closing the gap when
  // one is absent instead of leaving a skipped number.
  const hasSurveyResponses =
    !!surveyResponseComments?.hasComments &&
    (surveyResponseComments?.sessions?.length ?? 0) > 0;
  const showFinancialResult =
    !!financialSummary &&
    !primary?.isProgram &&
    reportInclusions.financialSummary !== false;
  const appendixNum = (() => {
    let n = 1;
    const evidence = n;
    const financial = showFinancialResult ? ++n : null;
    const survey = hasSurveyResponses ? ++n : null;
    const contractors = totalContractors > 0 ? ++n : null;
    return { evidence, financial, survey, contractors };
  })();

  // "Actions" in the Conclusions heading refers to the Next steps content —
  // drop it from the title when there's nothing there to action.
  const hasNextSteps =
    reportInclusions.nextSteps !== false &&
    !!(nextStepsOverride ?? primary?.nextSteps);

  const { recordType, managerType } = activityContext(t, primary);
  const statusLabel = primary
    ? STATUS[primary.statusName]?.label || primary.statusName
    : null;

  const orgPill = orgName ? `${orgName}${orgId ? ` (${orgId})` : ''}` : null;
  const contextPills = [orgPill, statusLabel, recordType, managerType].filter(
    Boolean
  );

  // ── Document preferences ─────────────────────────────────────────────────────
  const doc = prefs?.document || {};
  const useGraded = prefs?.outcomes?.useGraded || false;

  // Returns the correct outcome label for an objective or eval-plan outcome,
  // respecting the useGraded preference. Falls back to outcomeTitle if no
  // graded version has been defined for that outcome.
  const outcomeLabel = (obj) =>
    useGraded && obj?.graded ? obj.graded : obj?.outcomeTitle || '';
  const cover = doc.cover || {};
  const header = doc.header || {};
  const footer = doc.footer || {};
  const sections = doc.sections || {};

  const fontFamily = doc.fontFamily
    ? `${doc.fontFamily}, sans-serif`
    : 'Arial, sans-serif';
  const bodyFontSize = doc.bodyFontSize ? `${doc.bodyFontSize}pt` : undefined;

  const showCover = cover.enabled ?? true;

  // Cover image credit/description caption — sourced from the same
  // Documents & Media record (sProjectDocument.description/imageCredit), not
  // something entered again here. "Full bleed" (crop spans essentially the
  // whole page) needs a scrim behind the text since the photo underneath
  // could be any colour; anything smaller sits in the plain page margin
  // below-right of the photo instead, where a scrim isn't needed.
  const coverCaptionText = [coverImg?.description, coverImg?.imageCredit]
    .filter(Boolean)
    .join(' — ');
  const coverCropIsFullBleed =
    !!coverImg?.crop &&
    coverImg.crop.xPct <= 2 &&
    coverImg.crop.yPct <= 2 &&
    coverImg.crop.xPct + coverImg.crop.widthPct >= 98 &&
    coverImg.crop.yPct + coverImg.crop.heightPct >= 98;
  // The crop rect is allowed to overshoot the page (a photo can be zoomed/
  // panned past 100%) — clamped to 0 so the caption always sits fully
  // inside the visible page instead of being pushed past its edge and
  // clipped by .rb-cover-page's overflow:hidden.
  const coverCaptionRightPct = coverImg?.crop
    ? Math.max(0, 100 - (coverImg.crop.xPct + coverImg.crop.widthPct))
    : 0;
  const coverCaptionBottomPct = coverImg?.crop
    ? Math.max(0, 100 - (coverImg.crop.yPct + coverImg.crop.heightPct))
    : 0;
  const coverCaptionTopPct = coverImg?.crop
    ? Math.min(100, coverImg.crop.yPct + coverImg.crop.heightPct)
    : 0;
  const showCorporateStatement =
    (sections.corporateStatement ?? true) &&
    !!(prefs.publicStatementTitle || prefs.publicStatement);
  const showHeader = header.enabled ?? true;
  const showFooter = footer.enabled ?? true;
  const showTOC = sections.tableOfContents ?? false;

  // Section visibility from prefs (default true for main sections, false for optional)
  const SECTION_VISIBLE = {
    'Executive summary': sections.executiveSummary ?? true,
    'Strategic alignment': sections.strategicAlignment ?? true,
    'Intended impact and reasoning': sections.intendedImpact ?? true,
    Evaluation: sections.evaluation ?? true,
    'Conclusions and actions': sections.conclusions ?? true,
    Appendices: sections.appendices ?? false,
    Glossary: sections.glossary ?? false,
  };
  // Appendix 2 sub-toggle — independent of the Appendices section itself so
  // an org can keep Appendix 1 (Evidence) but suppress respondent comments,
  // or vice versa. Default on, per the report-builder Preferences default.
  const showSurveyResponsesAppendix = sections.surveyResponsesAppendix ?? true;

  // ── Objective processing ─────────────────────────────────────────────────────

  // Detect which objectives have evidence fully shared with a later objective
  const evidenceSharedWithLater = objectives
    ? objectives.map(
        (obj, i) =>
          Boolean(obj.evidence?.length) &&
          objectives
            .slice(i + 1)
            .some((later) =>
              obj.evidence.every((ev) =>
                later.evidence?.some((lev) => lev.content === ev.content)
              )
            )
      )
    : [];

  // ── TOC entries ──────────────────────────────────────────────────────────────
  // Build a flat list of level-1 and level-2 TOC entries from the visible sections.

  // A custom-section heading (if added at that break) becomes its own
  // level-1 Contents entry, positioned right where it renders in the body.
  const customTocEntry = (slot) => {
    const cs = customisation.customSections?.[slot];
    if (!cs?.has?.heading) return false;
    return {
      level: 1,
      text: cs.heading?.trim() || t('report.builder.untitled_section'),
      anchorId: `custom-${slot}`,
    };
  };

  const tocEntries = [
    SECTION_VISIBLE['Executive summary'] !== false && {
      level: 1,
      text: t('report.builder.executive_summary.title'),
      anchorId: 'sec-exec',
    },
    SECTION_VISIBLE['Executive summary'] !== false && {
      level: 2,
      text: t('report.builder.preview.executive_summary.background'),
      parentAnchorId: 'sec-exec',
    },
    SECTION_VISIBLE['Executive summary'] !== false &&
      customTocEntry('exec-background'),
    SECTION_VISIBLE['Executive summary'] !== false && {
      level: 2,
      text: t('report.builder.preview.result'),
      parentAnchorId: 'sec-exec',
    },
    SECTION_VISIBLE['Executive summary'] !== false &&
      customTocEntry('conc-result-notes'),
    SECTION_VISIBLE['Strategic alignment'] !== false && {
      level: 1,
      text: t('report.builder.strategic_alignment'),
      anchorId: 'sec-strategic',
    },
    SECTION_VISIBLE['Strategic alignment'] !== false &&
      primary?.goal && {
        level: 2,
        text: t('report.builder.preview.org_goal'),
        parentAnchorId: 'sec-strategic',
      },
    SECTION_VISIBLE['Strategic alignment'] !== false &&
      primary?.goalDescription && {
        level: 2,
        text: t('common.goal_description'),
        parentAnchorId: 'sec-strategic',
      },
    SECTION_VISIBLE['Strategic alignment'] !== false &&
      crossRefs && {
        level: 2,
        text: crossRefs.heading || t('report.builder.preview.cross_reference'),
        parentAnchorId: 'sec-strategic',
      },
    SECTION_VISIBLE['Intended impact and reasoning'] !== false && {
      level: 1,
      text: t('report.builder.preview.intended_impact.intended_impact_title'),
      anchorId: 'sec-intended',
    },
    ...(SECTION_VISIBLE['Intended impact and reasoning'] !== false && objectives
      ? objectives.flatMap((obj, i) =>
          [
            {
              level: 2,
              text: `${t('report.builder.preview.intended_impact.intended_domain_outcome', { domain: obj.domain ? obj.domain.toLowerCase() + ' ' : '', outcome: outcomeLabel(obj) })}`,
              parentAnchorId: 'sec-intended',
            },
            customTocEntry(`toc-${i}`),
          ].filter(Boolean)
        )
      : []),
    SECTION_VISIBLE['Evaluation'] !== false && {
      level: 1,
      text: t('common.evaluation'),
      anchorId: 'sec-eval',
    },
    SECTION_VISIBLE['Evaluation'] !== false && {
      level: 2,
      text: t('report.builder.preview.fast_facts'),
      parentAnchorId: 'sec-eval',
    },
    SECTION_VISIBLE['Evaluation'] !== false &&
      customTocEntry('fastfacts-notes'),
    SECTION_VISIBLE['Evaluation'] !== false && {
      level: 2,
      text: t('report.builder.preview.methodology'),
      parentAnchorId: 'sec-eval',
    },
    SECTION_VISIBLE['Evaluation'] !== false &&
      customTocEntry('eval-methodology-notes'),
    ...(SECTION_VISIBLE['Evaluation'] !== false && objectives
      ? objectives.map((obj) => ({
          level: 2,
          text: outcomeLabel(obj),
          parentAnchorId: 'sec-eval',
        }))
      : []),
    // Per-plan/per-outcome custom headings — evaluationPlans (used by the
    // body's analysis-notes-{pi}-{oi} slots) isn't the same list as
    // objectives above, so these are appended as their own flat run rather
    // than interleaved 1:1 with the per-outcome entries.
    ...(SECTION_VISIBLE['Evaluation'] !== false && evaluationPlans
      ? evaluationPlans.flatMap((plan, pi) =>
          (plan.outcomes || [])
            .map((oc, oi) => customTocEntry(`analysis-notes-${pi}-${oi}`))
            .filter(Boolean)
        )
      : []),
    SECTION_VISIBLE['Conclusions and actions'] !== false && {
      level: 1,
      text: t('report.builder.preview.conclusion'),
      anchorId: 'sec-conclusion',
    },
    SECTION_VISIBLE['Conclusions and actions'] !== false && {
      level: 2,
      text: t('report.builder.preview.findings'),
      parentAnchorId: 'sec-conclusion',
    },
    SECTION_VISIBLE['Conclusions and actions'] !== false &&
      customTocEntry('exec-findings'),
    SECTION_VISIBLE['Conclusions and actions'] !== false && {
      level: 2,
      text: t('report.builder.preview.reflection'),
      parentAnchorId: 'sec-conclusion',
    },
    SECTION_VISIBLE['Conclusions and actions'] !== false &&
      customTocEntry('conc-reflection'),
    SECTION_VISIBLE['Conclusions and actions'] !== false && {
      level: 2,
      text: t('report.builder.preview.next_steps'),
      parentAnchorId: 'sec-conclusion',
    },
    SECTION_VISIBLE['Conclusions and actions'] !== false &&
      customTocEntry('conc-nextsteps-notes'),
    SECTION_VISIBLE['Appendices'] !== false && {
      level: 1,
      text: t('report.builder.preferences.appendices'),
      anchorId: 'sec-appendices',
    },
    SECTION_VISIBLE['Appendices'] !== false && {
      level: 2,
      text: `${t('report.builder.preview.appendix')} ${appendixNum.evidence}: ${t('common.evidence')}`,
      parentAnchorId: 'sec-appendices',
    },
    SECTION_VISIBLE['Appendices'] !== false &&
      showFinancialResult && {
        level: 2,
        text: `${t('report.builder.preview.appendix')} ${appendixNum.financial}: ${t('activity.documentation.financialSummary.financial_result')}`,
        parentAnchorId: 'sec-appendices',
      },
    SECTION_VISIBLE['Appendices'] !== false &&
      hasSurveyResponses && {
        level: 2,
        text: `${t('report.builder.preview.appendix')} ${appendixNum.survey}: ${t('report.builder.preview.survey_responses')}`,
        parentAnchorId: 'sec-appendices',
      },
    SECTION_VISIBLE['Appendices'] !== false &&
      totalContractors > 0 && {
        level: 2,
        text: `${t('report.builder.preview.appendix')} ${appendixNum.contractors}: ${t('common.contractors')}`,
        parentAnchorId: 'sec-appendices',
      },
    SECTION_VISIBLE['Glossary'] !== false && {
      level: 1,
      text: t('report.builder.preview.glossary'),
      anchorId: 'sec-glossary',
    },
  ].filter(Boolean);

  // Derive TOC page numbers from computed break positions.
  // Level-1 entries are measured directly; level-2 entries inherit their parent section's page.
  const tocPageNumbers = useMemo(() => {
    if (!paperContentRef.current || pageLines.length === 0) return {};
    const paperRect =
      paperContentRef.current.parentElement.getBoundingClientRect();
    const result = {};
    const anchorIds = [
      'sec-exec',
      'sec-strategic',
      'sec-intended',
      'sec-eval',
      'sec-conclusion',
      'sec-appendices',
      'sec-glossary',
      // Custom-section headings — one per break that has an active heading.
      ...tocEntries
        .filter((e) => e.level === 1 && e.anchorId?.startsWith('custom-'))
        .map((e) => e.anchorId),
    ];
    anchorIds.forEach((id) => {
      const el = paperContentRef.current.querySelector(`#${id}`);
      if (!el) return;
      const top = el.getBoundingClientRect().top - paperRect.top;
      result[id] = pageLines.filter((l) => l <= top).length + 1;
    });
    return result;
  }, [pageLines, tocEntries]);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* ── Action bar ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
              {t('report.builder.report_preview')}
            </div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 11,
                color: '#6b7280',
                fontWeight: 400,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={showDataTint}
                onChange={(e) => setShowDataTint(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              {t('report.builder.preview.show_non_editable_text')}
            </label>
            {onEdit && (
              <button
                onClick={onEdit}
                style={{
                  fontSize: 12,
                  color: '#1d4ed8',
                  background: 'none',
                  border: '1px solid #1d4ed8',
                  borderRadius: 6,
                  padding: '2px 10px',
                  cursor: 'pointer',
                }}
              >
                ← {t('report.builder.selection')}
              </button>
            )}
          </div>
          {/* Admin (Super Admin) Report Builder only — onEdit is undefined
              for the embedded single-activity slideout, where the activity
              is already identified elsewhere on the page. */}
          {onEdit && (
            <div
              style={{
                fontSize: 12,
                color: '#6b7280',
                marginTop: 2,
                fontWeight: 700,
              }}
            >
              {names.length === 1
                ? `${names[0]}${primary?.id ? ` (#${primary.id})` : ''}`
                : `${names[0]} + ${names.length - 1} ${t('common.more_as_in_6_more')}`}
            </div>
          )}
          {onEdit && contextPills.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginTop: 6,
              }}
            >
              {contextPills.map((pill, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '2px 9px',
                    borderRadius: 999,
                    background: '#f1f5f9',
                    color: '#374151',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  {pill}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Download button — deferred to next phase */}
        <button
          disabled
          title={t('report.builder.preview.export.word_export_coming_soon')}
          style={{
            background: '#e5e7eb',
            color: '#9ca3af',
            border: 'none',
            borderRadius: 8,
            padding: '9px 18px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'not-allowed',
          }}
        >
          ⬇ {t('report.builder.preview.export.downlaod_report_docx')}
        </button>
      </div>
      {/* ── A4 document preview ── */}
      {/* Outer wrapper constrains to A4 width (794px = 210mm at 96dpi), even
          inside a wider SlideoutPanel (e.g. xwide) — the PageBreaks preview
          below computes page-break positions from the rendered height of
          content AT THE CURRENT WIDTH, and the real export is a fixed
          794px-wide A4 page. Letting the paper stretch wider makes text
          wrap into fewer/wider lines, so the page-break markers drift from
          where pages will actually break in the exported Word/PDF — capping
          the width keeps the preview an accurate WYSIWYG guide regardless
          of how wide the surrounding slideout is.                        */}
      {/* On viewports narrower than 794px the paper shrinks to fit (width:100%). */}
      <div style={{ width: '100%', maxWidth: A4_W_PX }}>
        <div
          className={`rb-paper report-doc${showDataTint ? '' : ' rb-no-tint'}`}
          style={{
            position: 'relative',
            background: '#fff',
            boxShadow: '0 2px 16px rgba(0,0,0,0.12)',
            borderRadius: 4,
            width: '100%',
            fontFamily,
            fontSize: bodyFontSize || REPORT_FONT.body,
            '--doc-body-size': bodyFontSize || `${REPORT_FONT.body}px`,
          }}
        >
          <PageBreaks
            contentRef={paperContentRef}
            onLinesChange={setPageLines}
          />
          {/* Header bar */}
          {showHeader && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 56px',
                borderBottom: '1px solid #e5e7eb',
                fontSize: REPORT_FONT.note,
                color: '#6b7280',
              }}
            >
              <span>{header.leftText || ''}</span>
              <span>{header.rightText || ''}</span>
            </div>
          )}

          <div ref={paperContentRef} style={{ padding: '48px 56px' }}>
            <ReportTintContext.Provider value={showDataTint}>
              {/* Cover page */}
              {showCover && (
                <>
                  {/* Full-A4 cover page with optional background image */}
                  <div
                    className="rb-cover-page"
                    style={{
                      position: 'relative',
                      width: 'calc(100% + 112px)',
                      height: A4_H_PX,
                      margin: '-48px -56px 48px -56px',
                      overflow: 'hidden',
                      backgroundColor: coverImg ? undefined : '#f3f4f6',
                    }}
                  >
                    {/* Crop line — where on the page the photo shows (% of
                        the page's own size), chosen in CoverImageCropModal.
                        Fully independent of the photo's own pan/zoom (also
                        % of the page), so cropping never rescales/distorts
                        the photo — it only changes how much of it shows.
                        Clips the photo to its own bounds. */}
                    {coverImg?.crop && coverImg?.image && (
                      <div
                        style={{
                          position: 'absolute',
                          left: `${coverImg.crop.xPct}%`,
                          top: `${coverImg.crop.yPct}%`,
                          width: `${coverImg.crop.widthPct}%`,
                          height: `${coverImg.crop.heightPct}%`,
                          overflow: 'hidden',
                          backgroundColor:
                            prefs?.coverImageBackground?.colour === null
                              ? 'transparent'
                              : prefs?.coverImageBackground?.colour ||
                                '#1f2937',
                          pointerEvents: 'none',
                        }}
                      >
                        {/* Photo — both image and crop are % of the PAGE, so
                            the photo's page-relative position/size has to be
                            re-expressed as a percentage of the crop div's
                            own box (the containing block CSS percentages
                            resolve against) to land in the same place: e.g.
                            (image.xPct - crop.xPct) / crop.widthPct * 100
                            is "how far into the crop box, as a % of the crop
                            box's own width, the photo's left edge falls." */}
                        <img
                          src={coverImg.url}
                          alt=""
                          style={{
                            position: 'absolute',
                            left: `${
                              ((coverImg.image.xPct - coverImg.crop.xPct) /
                                coverImg.crop.widthPct) *
                              100
                            }%`,
                            top: `${
                              ((coverImg.image.yPct - coverImg.crop.yPct) /
                                coverImg.crop.heightPct) *
                              100
                            }%`,
                            width: `${
                              (coverImg.image.widthPct /
                                coverImg.crop.widthPct) *
                              100
                            }%`,
                            height: `${
                              (coverImg.image.heightPct /
                                coverImg.crop.heightPct) *
                              100
                            }%`,
                            maxWidth: 'none',
                          }}
                        />
                      </div>
                    )}

                    {/* Credit/description caption — bottom-right of the
                        photo. Full-bleed cover: overlaid on the photo itself
                        (contrasting white text on a 50% dark scrim, since
                        the photo underneath could be any colour). Smaller/
                        repositioned cover: sits in the plain page margin
                        just below-right of the photo instead, where a scrim
                        isn't needed. Explicit z-index on both variants —
                        without it, the activity-name/org-name overlay
                        (rendered later in the DOM, also pinned to the
                        page's bottom) paints over this caption once the
                        cover photo is full-bleed and the two start
                        occupying the same bottom-of-page area. */}
                    {coverImg?.crop && coverCaptionText && (
                      <div
                        style={
                          coverCropIsFullBleed
                            ? {
                                position: 'absolute',
                                right: `${coverCaptionRightPct}%`,
                                bottom: `${coverCaptionBottomPct}%`,
                                maxWidth: '70%',
                                margin: 8,
                                padding: '3px 8px',
                                background: 'rgba(0, 0, 0, 0.5)',
                                borderRadius: 3,
                                color: '#ffffff',
                                fontSize: 10,
                                lineHeight: 1.4,
                                textAlign: 'right',
                                zIndex: 11,
                              }
                            : {
                                position: 'absolute',
                                right: `${coverCaptionRightPct}%`,
                                top: `${coverCaptionTopPct}%`,
                                maxWidth: '70%',
                                marginTop: 4,
                                color: '#6b7280',
                                fontSize: 10,
                                lineHeight: 1.4,
                                textAlign: 'right',
                                zIndex: 11,
                              }
                        }
                      >
                        {coverCaptionText}
                      </div>
                    )}

                    {/* Image picker / change button, plus Reposition next to
                        it — grouped in one top-left flex row so Reposition
                        always sits directly beside Change image regardless
                        of the first button's text width. */}
                    <div className="rb-cover-page__top-actions">
                      <button
                        type="button"
                        className="rb-cover-page__img-btn"
                        onClick={() => openImagePicker(null)}
                        title={
                          coverImg
                            ? t(
                                'report.builder.cover_image_editing.change_cover_image'
                              )
                            : t(
                                'report.builder.cover_image_editing.add_cover_image'
                              )
                        }
                      >
                        {coverImg
                          ? `⇄ ${t('report.builder.cover_image_editing.change_image')}`
                          : `+ ${t('report.builder.cover_image_editing.add_cover_image')}`}
                      </button>

                      {/* Reposition/zoom — reopens the crop modal on the
                          already-saved image, seeded with its saved crop/zoom */}
                      {coverImg && (
                        <button
                          type="button"
                          className="rb-cover-page__reposition-btn"
                          onClick={() => setShowCoverCropModal(true)}
                          title={t(
                            'report.builder.cover_image_editing.reposition_or_zoom'
                          )}
                        >
                          ⤢ {t('report.builder.cover_image_editing.reposition')}
                        </button>
                      )}
                    </div>

                    {/* Remove image button */}
                    {coverImg && (
                      <button
                        type="button"
                        className="rb-cover-page__remove-btn"
                        onClick={() => {
                          setCoverImg(null);
                          saveCoverImage(null);
                        }}
                        title={t('report.builder.remove_cover_image')}
                      >
                        ✕
                      </button>
                    )}

                    {/* Text overlay — pinned to bottom of cover page */}
                    <div className="rb-cover-page__overlay">
                      {/* Report header (cover.tagline) — conditional */}
                      {cover.tagline && (
                        <div className="rb-cover-page__tagline">
                          {cover.tagline}
                        </div>
                      )}

                      {/* Activity name — title style with border */}
                      <div className="rb-cover-page__activity-name">
                        {primary?.name || `[${t('report.activity_name')}]`}
                      </div>

                      {/* Organisation name — editable, saves to prefs */}
                      <div
                        contentEditable
                        suppressContentEditableWarning
                        className="rb-cover-page__org-name"
                        onBlur={async (e) => {
                          const value = e.currentTarget.innerText.trim();
                          const singleActivityId =
                            selectedActivities?.length === 1
                              ? primaryActivityId
                              : null;
                          const prefsParams = singleActivityId
                            ? { activityIds: singleActivityId }
                            : orgId
                              ? { orgId }
                              : null;
                          if (!prefsParams) return;
                          try {
                            const res = await axios.get(
                              '/report-builder/preferences',
                              { params: prefsParams }
                            );
                            const existing = res.data?.orgOverrides || {};
                            await axios.put(
                              '/report-builder/preferences',
                              {
                                ...existing,
                                document: {
                                  ...(existing.document || {}),
                                  cover: {
                                    ...(existing.document?.cover || {}),
                                    orgName: value || null,
                                  },
                                },
                              },
                              { params: prefsParams }
                            );
                          } catch (err) {
                            console.error('Failed to save cover org name', err);
                          }
                        }}
                      >
                        {cover.orgName ||
                          orgName ||
                          `[${t('formulary.organisation_name')}]`}
                      </div>
                    </div>
                  </div>

                  {/* Page break */}
                  <div
                    style={{
                      borderTop: '2px dashed #e5e7eb',
                      margin: '0 -56px 36px -56px',
                    }}
                  />
                </>
              )}

              {/* Title page + its page-break — hidden when a cover page is in use */}
              {!showCover && (
                <>
                  <div style={{ marginBottom: 36, paddingTop: 32 }}>
                    {/* Optional custom section above the title */}
                    <CustomSectionSlot
                      {...customSectionProps('title-page-above')}
                    />

                    {/* Activity title — editable, saves to sProject.name */}
                    <div style={{ position: 'relative', margin: '16px 0' }}>
                      <RecordBadge />
                      <div
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={async (e) => {
                          const name = e.currentTarget.innerText.trim();
                          if (!name || !primaryActivityId) return;
                          try {
                            await axios.put(
                              `/project/${primaryActivityId}/name`,
                              {
                                name,
                              }
                            );
                          } catch (err) {
                            console.error('Failed to save activity name', err);
                          }
                        }}
                        style={{
                          fontSize: 29,
                          fontFamily: 'Arial, sans-serif',
                          fontWeight: 700,
                          color: '#111827',
                          borderTop: '1.5px solid #111827',
                          borderBottom: '1.5px solid #111827',
                          paddingTop: 10,
                          paddingBottom: 10,
                          outline: 'none',
                          cursor: 'text',
                        }}
                      >
                        {primary?.name || `[${t('report.activity_name')}]`}
                      </div>
                    </div>

                    {/* Optional custom section below the title */}
                    <CustomSectionSlot
                      {...customSectionProps('title-page-below')}
                    />
                  </div>

                  {/* Page break visual separator */}
                  <div
                    style={{
                      borderTop: '2px dashed #e5e7eb',
                      margin: '0 -56px 28px -56px',
                    }}
                  />
                </>
              )}

              {/* Corporate public statement (pre-TOC, not in Contents) */}
              {showCorporateStatement && (
                <div
                  data-force-break
                  style={{
                    marginBottom: 28,
                    background: showDataTint ? '#f0fdf4' : 'transparent',
                    borderRadius: showDataTint ? 4 : 0,
                    padding: showDataTint ? '10px 12px' : 0,
                    breakBefore: 'page',
                    pageBreakBefore: 'always',
                  }}
                >
                  {prefs.publicStatementTitle && (
                    <div
                      style={{
                        fontSize: REPORT_FONT.h2,
                        fontWeight: REPORT_WEIGHT.h2,
                        color: showDataTint ? '#166534' : '#1e293b',
                        marginBottom: 8,
                        borderBottom: `1px solid ${
                          showDataTint ? '#d1fae5' : '#e5e7eb'
                        }`,
                        paddingBottom: 6,
                      }}
                    >
                      {prefs.publicStatementTitle}
                    </div>
                  )}
                  <div
                    className="rb-pub-stmt__body"
                    style={{
                      fontSize: REPORT_FONT.body,
                      lineHeight: 1.7,
                      color: showDataTint ? '#166534' : '#374151',
                    }}
                    dangerouslySetInnerHTML={{ __html: prefs.publicStatement }}
                  />
                </div>
              )}

              {/* Table of Contents */}
              {showTOC && (
                <div
                  data-force-break
                  data-break-guard="toc"
                  style={{
                    borderBottom: '2px solid #e5e7eb',
                    paddingBottom: 12,
                    marginBottom: 16,
                    background: showDataTint ? '#fff9e3' : 'transparent',
                    borderRadius: showDataTint ? 4 : 0,
                    padding: showDataTint ? '10px 12px 12px' : '0 0 12px',
                    breakInside: 'avoid',
                    pageBreakInside: 'avoid',
                  }}
                >
                  <div
                    style={{
                      fontSize: 21,
                      fontWeight: 700,
                      marginBottom: 10,
                      color: '#111827',
                    }}
                  >
                    {t('report.builder.table_of_contents_label_short')}
                  </div>
                  {tocEntries.map((entry, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        paddingLeft: entry.level === 2 ? 16 : 0,
                        fontSize:
                          entry.level === 1 ? REPORT_FONT.h2 : REPORT_FONT.body,
                        fontWeight:
                          entry.level === 1
                            ? REPORT_WEIGHT.h2
                            : REPORT_WEIGHT.body,
                        color: '#111827',
                        marginBottom: 3,
                      }}
                    >
                      <span style={{ whiteSpace: 'nowrap' }}>{entry.text}</span>
                      <span
                        style={{
                          flex: 1,
                          borderBottom: '1px dotted #9ca3af',
                          margin: '0 6px 3px',
                        }}
                      />
                      <span style={{ whiteSpace: 'nowrap', color: '#9ca3af' }}>
                        {tocPageNumbers[
                          entry.anchorId || entry.parentAnchorId
                        ] ?? '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Body sections ─────────────────────────────────────────────── */}

              {/* ── Executive Summary ── */}
              {SECTION_VISIBLE['Executive summary'] !== false && (
                <CollapsibleSection
                  title={t('report.builder.executive_summary.title')}
                  forceBreak
                  anchorId="sec-exec"
                >
                  <SubHeading>
                    {t('report.builder.preview.executive_summary.background')}
                  </SubHeading>
                  <BackgroundTable activity={primary} venues={venues} />
                  {/* Activity description from Documentation page — sProject.summary */}
                  <SyncedField>
                    {reportInclusions.execSummary === false ? (
                      <p
                        style={{
                          margin: 0,
                          color: '#9ca3af',
                          fontStyle: 'italic',
                        }}
                      >
                        {t('report.builder.preview.text_available')}
                      </p>
                    ) : (
                      <WriterNoteHtml
                        key={`bg-description-${primary?.id}-${descVersion}`}
                        emptyPlaceholder={t(
                          'report.builder.preview.please_enter_final_activity_description'
                        )}
                        onBlur={async (html) => {
                          if (!primary?.id) return;
                          try {
                            await axios.put(
                              `/project/${primary.id}/description`,
                              {
                                description: html,
                              }
                            );
                            setDescOverride(html);
                            const ch = new BroadcastChannel('takso_activity');
                            ch.postMessage({
                              type: 'summary',
                              activityId: primary.id,
                              summary: html,
                            });
                            ch.close();
                          } catch (e) {
                            console.error(
                              'Failed to save activity description',
                              e
                            );
                          }
                        }}
                      >
                        {HtmlService.convertToInnerHtml({
                          children: descOverride ?? primary?.description,
                        })}
                      </WriterNoteHtml>
                    )}
                  </SyncedField>
                  {showWriterNote && (
                    <p style={{ margin: '8px 0 0' }}>
                      {dateRange(primary?.startDate, primary?.endDate)
                        ? `${t('report.builder.preview.this_activity_ran_on_dates', { dates: dateRange(primary?.startDate, primary?.endDate), deliveredBy: primary?.teamName ? `, delivered by {primary.teamName}` : '' })}`
                        : ''}
                      {t(
                        'report.builder.preview.this_activity_ran_on_dates_consider_naming'
                      )}
                    </p>
                  )}
                  <CustomSectionSlot
                    {...customSectionProps('exec-background')}
                  />

                  <SubHeading>{t('report.builder.preview.result')}</SubHeading>
                  {/* All shortlisted quotes — cross-outcome picker for Findings */}
                  <QuotePicker
                    quotes={shortlistedComments.all}
                    selectedIds={findingsQuoteIds}
                    onToggle={toggleFindingsQuote}
                    label={t(
                      'report.builder.preview.select_participant_quotes_for_findings'
                    )}
                    showTint={showDataTint}
                  />
                  <SyncedField>
                    {reportInclusions.result === false ? (
                      <p
                        style={{
                          margin: 0,
                          color: '#9ca3af',
                          fontStyle: 'italic',
                        }}
                      >
                        {t('report.builder.preview.text_available')}
                      </p>
                    ) : (
                      <WriterNoteHtml
                        key={`conc-result-${primary?.id}-${resultVersion}`}
                        emptyPlaceholder={t(
                          'report.builder.preview.no_text_entered'
                        )}
                        onBlur={async (html) => {
                          if (!primary?.id) return;
                          try {
                            await axios.put(`/project/${primary.id}/result`, {
                              result: html,
                            });
                            setResultOverride(html);
                            const ch = new BroadcastChannel('takso_activity');
                            ch.postMessage({
                              type: 'result',
                              activityId: primary.id,
                              result: html,
                            });
                            ch.close();
                          } catch (e) {
                            console.error('Failed to save result', e);
                          }
                        }}
                      >
                        {HtmlService.convertToInnerHtml({
                          children: resultOverride ?? primary?.result,
                        })}
                      </WriterNoteHtml>
                    )}
                  </SyncedField>
                  {showWriterNote && (
                    <p style={{ margin: '8px 0 0' }}>
                      {objectives?.length
                        ? `${t('report.builder.preview.activity_evaluated_against', { outcomes: t('report.builder.num_outcomes', { count: objectives.length }) })}
                            ${
                              objectives.length <= 3
                                ? ` (${objectives
                                    .map((o) => o.outcomeTitle)
                                    .filter(Boolean)
                                    .join(', ')})`
                                : ''
                            }. `
                        : ''}
                      {t(
                        'report.builder.preview.state_plainly_activity_achievments'
                      )}
                    </p>
                  )}
                  <CustomSectionSlot
                    {...customSectionProps('conc-result-notes')}
                  />
                </CollapsibleSection>
              )}

              {/* ── Strategic Alignment ── */}
              {SECTION_VISIBLE['Strategic alignment'] !== false && (
                <CollapsibleSection
                  title={t('report.builder.strategic_alignment')}
                  anchorId="sec-strategic"
                >
                  {/* "Organisational goal" — template heading: blue tint via SubHeading */}
                  <SubHeading>
                    {t('report.builder.preview.org_goal')}
                  </SubHeading>
                  {/* Goal value — org-defined in Organisation > Settings: green tint */}
                  <DataPanel tight>
                    {primary?.goal ? (
                      <p
                        style={{
                          fontSize: REPORT_FONT.body,
                          fontFamily: 'Arial, sans-serif',
                          margin: 0,
                          lineHeight: 1.5,
                        }}
                      >
                        {primary.goal}
                      </p>
                    ) : (
                      <div
                        style={{
                          fontSize: REPORT_FONT.note,
                          fontStyle: 'italic',
                          borderLeft: '2px solid #d1fae5',
                          paddingLeft: 8,
                        }}
                      >
                        [{t('report.builder.preview.no_org_goal_recorded')}]
                      </div>
                    )}
                  </DataPanel>

                  {/* "Goal description" — template heading: blue tint via SubHeading */}
                  <SubHeading>{t('common.goal_description')}</SubHeading>
                  {/* Description value — org-defined in Organisation > Settings: green tint */}
                  <DataPanel tight>
                    {primary?.goalDescription ? (
                      <p
                        style={{
                          fontSize: REPORT_FONT.body,
                          fontFamily: 'Arial, sans-serif',
                          margin: 0,
                          lineHeight: 1.5,
                        }}
                      >
                        {primary.goalDescription}
                      </p>
                    ) : (
                      <div
                        style={{
                          fontSize: REPORT_FONT.note,
                          fontStyle: 'italic',
                          borderLeft: '2px solid #d1fae5',
                          paddingLeft: 8,
                        }}
                      >
                        [
                        {t(
                          'report.builder.preview.no_goal_description_recorded'
                        )}
                        ]
                      </div>
                    )}
                  </DataPanel>

                  {crossRefs && (
                    // Cross-reference heading and values are org-defined (from
                    // Organisation > Settings) and cannot be edited in the report.
                    // Rendered as Category 2 — non-editable DB data (green tint).
                    <DataPanel>
                      <div
                        style={{
                          fontSize: REPORT_FONT.h2,
                          fontWeight: REPORT_WEIGHT.h2,
                          marginBottom: 6,
                        }}
                      >
                        {crossRefs.heading ||
                          t('report.builder.preview.cross_reference')}
                      </div>
                      {crossRefs.items?.map((item, ci) => (
                        <p
                          key={ci}
                          style={{
                            fontSize: REPORT_FONT.body,
                            fontFamily: 'Arial, sans-serif',
                            margin: '0 0 4px',
                            lineHeight: 1.5,
                          }}
                        >
                          {item}
                        </p>
                      ))}
                    </DataPanel>
                  )}
                </CollapsibleSection>
              )}

              {/* ── Intended Impact and Reasoning ── */}
              {SECTION_VISIBLE['Intended impact and reasoning'] !== false && (
                <CollapsibleSection
                  title={t(
                    'report.builder.preview.intended_impact_and_reasoning'
                  )}
                  anchorId="sec-intended"
                >
                  {objectives?.length > 0 ? (
                    objectives.map((obj, i) => {
                      const hasToC =
                        obj?.outcomeTitle || obj?.tocKnown || obj?.tocDo;
                      return (
                        <div key={i} style={{ paddingLeft: 0 }}>
                          <SubHeading>
                            {t(
                              'report.builder.preview.intended_domain_outcome',
                              {
                                domain: obj.domain
                                  ? obj.domain.toLowerCase()
                                  : '',
                                outcome: outcomeLabel(obj),
                              }
                            )}
                          </SubHeading>

                          <L3Heading>{t('common.theory_of_change')}</L3Heading>
                          {hasToC ? (
                            <div style={{ marginTop: 4, paddingLeft: 12 }}>
                              <TocLine
                                label={`${t('common.we_are_aiming_for')} `}
                                body={
                                  // outcomeContentFormat (sOutcome.contentFormat)
                                  // is written to read on from "aiming for ___",
                                  // unlike outcomeTitle (sOutcome.title), which is
                                  // a noun-phrase heading — matches the Evaluation
                                  // page's own Theory of Change sentence.
                                  obj?.outcomeContentFormat ||
                                  (() => {
                                    const label = outcomeLabel(obj);
                                    return label
                                      ? label.charAt(0).toLowerCase() +
                                          label.slice(1)
                                      : null;
                                  })()
                                }
                                ph={`[${t('report.builder.preview.outcome_measure')}]`}
                              />
                              <TocLine
                                label={`${t('report.builder.preview.we_know_that')} `}
                                body={obj?.tocKnown}
                                ph={`[${t('report.builder.preview.evidence_based_insight')}]`}
                              />
                              <TocLine
                                label={`${t('report.builder.preview.therefore_we_will')} `}
                                body={obj?.tocDo}
                                ph={`[${t('report.builder.preview.planned_approach')}]`}
                              />
                            </div>
                          ) : (
                            <div
                              style={{
                                marginTop: 4,
                                fontSize: REPORT_FONT.note,
                                fontStyle: 'italic',
                                fontFamily: 'Arial, sans-serif',
                                ...PH,
                                borderLeft: '2px solid #f3f4f6',
                                paddingLeft: 8,
                              }}
                            >
                              [
                              {t(
                                'report.builder.preview.theory_of_change_not_yet_recorded'
                              )}
                              ]
                            </div>
                          )}
                          <CustomSectionSlot
                            {...customSectionProps(`toc-${i}`)}
                          />

                          {!evidenceSharedWithLater?.[i] && (
                            <>
                              <L3Heading>{t('common.evidence')}</L3Heading>
                              {(() => {
                                const evTypes = [
                                  ...new Set(
                                    (obj?.evidence ?? [])
                                      .map((ev) => ev.type)
                                      .filter(Boolean)
                                  ),
                                ];
                                return evTypes.length ? (
                                  <div
                                    style={{ marginTop: 4, paddingLeft: 12 }}
                                  >
                                    <p
                                      style={{
                                        fontSize: REPORT_FONT.body,
                                        fontFamily: 'Arial, sans-serif',
                                        color: '#111827',
                                        margin: '0 0 4px',
                                        lineHeight: 1.5,
                                        background: showDataTint
                                          ? '#fff9e3'
                                          : 'transparent',
                                        borderRadius: showDataTint ? 2 : 0,
                                        padding: showDataTint ? '1px 3px' : 0,
                                      }}
                                    >
                                      {t(
                                        'report.builder.preview.our_evidence_for_this_theiry_includes'
                                      )}
                                    </p>
                                    <ul
                                      style={{
                                        margin: '0 0 0 18px',
                                        padding: 0,
                                        background: showDataTint
                                          ? '#f0fdf4'
                                          : 'transparent',
                                        borderRadius: showDataTint ? 3 : 0,
                                      }}
                                    >
                                      {evTypes.map((t, ti) => (
                                        <li
                                          key={ti}
                                          style={{
                                            fontSize: REPORT_FONT.body,
                                            fontFamily: 'Arial, sans-serif',
                                            color: '#111827',
                                            lineHeight: 1.5,
                                          }}
                                        >
                                          {t}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : null;
                              })()}
                            </>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        marginTop: 8,
                        fontSize: REPORT_FONT.note,
                        fontStyle: 'italic',
                        color: '#9ca3af',
                      }}
                    >
                      [{t('report.builder.preview.no_objectives_recorded')}]
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {/* ── Evaluation ── */}
              {SECTION_VISIBLE['Evaluation'] !== false && (
                <CollapsibleSection
                  title={t('common.evaluation')}
                  anchorId="sec-eval"
                >
                  {/* Fast facts first */}
                  <SubHeading>
                    {t('report.builder.preview.fast_facts')}
                  </SubHeading>
                  <FastFactsTable
                    fastFacts={fastFacts}
                    headerBg={
                      prefs?.bannerColour?.enabled
                        ? prefs?.bannerColour?.colour || '#ffffff'
                        : '#ffffff'
                    }
                    showTint={showDataTint}
                  />
                  <CustomSectionSlot
                    {...customSectionProps('fastfacts-notes')}
                  />

                  <SubHeading>
                    {t('report.builder.preview.methodology')}
                  </SubHeading>
                  <SyncedField>
                    {reportInclusions.methodology === false ? (
                      <p
                        style={{
                          margin: 0,
                          color: '#9ca3af',
                          fontStyle: 'italic',
                        }}
                      >
                        {t('report.builder.preview.text_available')}
                      </p>
                    ) : (
                      <WriterNoteHtml
                        key={`methodology-${primary?.id}-${methodologyVersion}`}
                        emptyPlaceholder={t(
                          'report.builder.preview.no_text_entered'
                        )}
                        onBlur={async (text) => {
                          if (!primary?.id) return;
                          try {
                            await axios.put(
                              `/project/${primary.id}/methodology`,
                              {
                                evaluationMethodology: text,
                              }
                            );
                            setMethodologyOverride(text);
                            const ch = new BroadcastChannel('takso_activity');
                            ch.postMessage({
                              type: 'methodology',
                              activityId: primary.id,
                              evaluationMethodology: text,
                            });
                            ch.close();
                          } catch (err) {
                            console.error('Failed to save methodology', err);
                          }
                        }}
                      >
                        {HtmlService.convertToInnerHtml({
                          children:
                            methodologyOverride ??
                            primary?.evaluationMethodology ??
                            '',
                        })}
                      </WriterNoteHtml>
                    )}
                  </SyncedField>
                  <CustomSectionSlot
                    {...customSectionProps('eval-methodology-notes')}
                  />

                  {/* ── Evaluation plan sections ──────────────────────────────────
                  One section per Evaluation Plan record in the Takso activity.
                  The plan name (from the Takso record) is the H2 heading.
                  Within each plan, outcomes are listed with:
                    1. In the words of participants
                    2. Analysis
                    3. Pre/post or post-only chart
                  ─────────────────────────────────────────────────────────────── */}
                  {evaluationPlans && evaluationPlans.length > 0 ? (
                    evaluationPlans.map((plan, pi) => (
                      <div key={plan.planId ?? pi}>
                        {/* Evaluation plan name — editable, saves to sevaluationplans.name */}
                        <SubHeading>
                          <span
                            contentEditable
                            suppressContentEditableWarning
                            onBlur={async (e) => {
                              const name = e.currentTarget.innerText.trim();
                              if (!name || !primaryActivityId) return;
                              try {
                                await axios.put(
                                  `/report-builder/eval-plan/${plan.planId}/name`,
                                  { name },
                                  { params: { activityId: primaryActivityId } }
                                );
                              } catch (err) {
                                console.error('Failed to save plan name', err);
                              }
                            }}
                            style={{ outline: 'none', cursor: 'text' }}
                          >
                            {plan.planName}
                          </span>
                        </SubHeading>

                        {plan.outcomes &&
                          plan.outcomes.length > 0 &&
                          plan.outcomes.map((oc, oi) => {
                            const domainLabel = oc.domain
                              ? oc.domain.charAt(0).toUpperCase() +
                                oc.domain.slice(1).toLowerCase()
                              : null;
                            return (
                              <div key={oi} style={{ marginTop: 12 }}>
                                {/* Outcome measure — read-only DB data; tinted when toggle is on */}
                                <div
                                  style={{
                                    fontSize: REPORT_FONT.h3,
                                    fontWeight: REPORT_WEIGHT.h3,
                                    color: showDataTint ? '#166534' : '#111827',
                                    background: showDataTint
                                      ? '#f0fdf4'
                                      : 'transparent',
                                    marginTop: 12,
                                    marginBottom: 4,
                                    padding: showDataTint ? '2px 6px' : '0',
                                    borderRadius: showDataTint ? 3 : 0,
                                  }}
                                >
                                  {domainLabel
                                    ? `${domainLabel} ${t('common.outcome').toLowerCase()}: `
                                    : `${t('common.outcome')}: `}
                                  {outcomeLabel(oc)}
                                </div>

                                <L3Heading>
                                  {t(
                                    'report.builder.preview.in_the_words_of_participants'
                                  )}
                                </L3Heading>
                                {/* Quotes filtered to this evaluation plan × outcome only */}
                                <QuotePicker
                                  quotes={(
                                    shortlistedComments.all || []
                                  ).filter(
                                    (q) =>
                                      q.planId === plan.planId &&
                                      q.outcomeId === oc.outcomeId
                                  )}
                                  selectedIds={
                                    participantQuoteIds[oc.evaluationId] || []
                                  }
                                  onToggle={(responseId) =>
                                    toggleParticipantQuote(
                                      oc.evaluationId,
                                      responseId
                                    )
                                  }
                                  label={t(
                                    'report.builder.preview.select_participant_quote'
                                  )}
                                  showTint={showDataTint}
                                  variant="bullet"
                                />

                                {/* Analysis — stored in sevaluation.reportNarrative */}
                                {/* Pre-populated from DB; onBlur saves back.        */}
                                <L3Heading>{t('common.analysis')}</L3Heading>
                                <SyncedField>
                                  <WriterNoteHtml
                                    key={`analysis-${oc.evaluationId}-${
                                      (oc.reportNarrative || '').length
                                    }-${
                                      narrativeOverrides[oc.evaluationId]
                                        ?.version ?? 0
                                    }`}
                                    onBlur={(text) =>
                                      saveNarrative(oc.evaluationId, text)
                                    }
                                  >
                                    {HtmlService.convertToInnerHtml({
                                      children:
                                        narrativeOverrides[oc.evaluationId]
                                          ?.text ?? oc.reportNarrative,
                                    })}
                                  </WriterNoteHtml>
                                </SyncedField>
                                <CustomSectionSlot
                                  {...customSectionProps(
                                    `analysis-notes-${pi}-${oi}`
                                  )}
                                />

                                {/* Outcome rating chart — Figure n */}
                                {(() => {
                                  const figN =
                                    evaluationPlans
                                      .slice(0, pi)
                                      .reduce(
                                        (acc, p) => acc + p.outcomes.length,
                                        0
                                      ) +
                                    oi +
                                    1;
                                  const figLabel = useGraded
                                    ? oc.graded || oc.outcomeTitle
                                    : oc.outcomeTitle;
                                  // chartImages is the live map kept in sync by
                                  // the Charts tab's Include toggle — reading it
                                  // here (instead of the one-time customisation
                                  // fetch) keeps this preview in sync when a
                                  // chart is toggled without needing a reload.
                                  const png =
                                    chartImages[oc.evaluationId] || null;
                                  return (
                                    <div style={{ marginTop: 12 }}>
                                      <L3Heading>
                                        {t('report.builder.preview.figure')}{' '}
                                        {figN}: {figLabel}
                                      </L3Heading>
                                      {png ? (
                                        <img
                                          src={png}
                                          alt={`Figure ${figN}: ${figLabel}`}
                                          style={{
                                            width: '100%',
                                            display: 'block',
                                            borderRadius: 4,
                                          }}
                                        />
                                      ) : (
                                        <div
                                          style={{
                                            padding: '16px',
                                            background: '#f3f4f6',
                                            borderRadius: 4,
                                            fontSize: 12,
                                            color: '#9ca3af',
                                            fontStyle: 'italic',
                                            textAlign: 'center',
                                          }}
                                        >
                                          {t(
                                            'report.builder.preview.chart_not_captured'
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                                {/* Individual Responses data — wired in next phase */}
                              </div>
                            );
                          })}
                      </div>
                    ))
                  ) : (
                    /* Placeholder until evaluation plan data is wired in */
                    <div
                      style={{
                        padding: '12px 16px',
                        background: '#f9fafb',
                        borderRadius: 6,
                        fontSize: 13,
                        color: '#9ca3af',
                        marginTop: 12,
                      }}
                    >
                      {t('report.builder.preview.evaluation_plans.placeholder')}
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {/* ── Conclusion ── */}
              {SECTION_VISIBLE['Conclusions and actions'] !== false && (
                <CollapsibleSection
                  title={
                    hasNextSteps
                      ? t(
                          'report.builder.preview.conclusions_and_actions.title'
                        )
                      : t('report.builder.preview.conclusions')
                  }
                  anchorId="sec-conclusion"
                >
                  <SubHeading>
                    {t('report.builder.preview.findings')}
                  </SubHeading>
                  {/* Findings text — sProject.analysis */}
                  <SyncedField>
                    {reportInclusions.findings === false ? (
                      <p
                        style={{
                          margin: 0,
                          color: '#9ca3af',
                          fontStyle: 'italic',
                        }}
                      >
                        {t('report.builder.preview.text_available')}
                      </p>
                    ) : (
                      <WriterNoteHtml
                        key={`findings-analysis-${primary?.id}-${analysisVersion}`}
                        emptyPlaceholder={t(
                          'report.builder.preview.no_text_entered'
                        )}
                        onBlur={async (text) => {
                          if (!primary?.id) return;
                          try {
                            await axios.put(`/project/${primary.id}/analysis`, {
                              analysis: text,
                            });
                            setAnalysisOverride(text);
                            const ch = new BroadcastChannel('takso_activity');
                            ch.postMessage({
                              type: 'analysis',
                              activityId: primary.id,
                              analysis: text,
                            });
                            ch.close();
                          } catch (e) {
                            console.error(
                              'Failed to save activity analysis',
                              e
                            );
                          }
                        }}
                      >
                        {HtmlService.convertToInnerHtml({
                          children: analysisOverride ?? primary?.analysis,
                        })}
                      </WriterNoteHtml>
                    )}
                  </SyncedField>
                  {showWriterNote && (
                    <p style={{ margin: '8px 0 0' }}>
                      {shortlistedComments.all?.length
                        ? t(
                            'report.builder.preview.participant_quote_shortlist',
                            {
                              count: shortlistedComments.all.length,
                              participantQuotes: t(
                                'report.builder.preview.participant_quotes',
                                { count: shortlistedComments.all.length }
                              ),
                            }
                          )
                        : ''}
                      {t(
                        'report.builder.preview.conclusions_and_actions.draw_out_one_or_two_findings'
                      )}
                    </p>
                  )}
                  <CustomSectionSlot {...customSectionProps('exec-findings')} />

                  <SubHeading>
                    {t('report.builder.preview.reflection')}
                  </SubHeading>
                  <SyncedField>
                    {reportInclusions.reflection === false ? (
                      <p
                        style={{
                          margin: 0,
                          color: '#9ca3af',
                          fontStyle: 'italic',
                        }}
                      >
                        {t('report.builder.preview.text_available')}
                      </p>
                    ) : (
                      <WriterNoteHtml
                        key={`conc-reflection-${primary?.id}-${reflectionVersion}`}
                        emptyPlaceholder={t(
                          'report.builder.preview.please_complete_your_reflection'
                        )}
                        emptyPlaceholderClassName="wn-empty-required"
                        onBlur={async (text) => {
                          if (!primary?.id) return;
                          try {
                            await axios.put(
                              `/project/${primary.id}/reflection`,
                              {
                                reflection: text,
                              }
                            );
                            setReflectionOverride(text);
                            const ch = new BroadcastChannel('takso_activity');
                            ch.postMessage({
                              type: 'reflection',
                              activityId: primary.id,
                              reflection: text,
                            });
                            ch.close();
                          } catch (e) {
                            console.error('Failed to save reflection', e);
                          }
                        }}
                      >
                        {HtmlService.convertToInnerHtml({
                          children: reflectionOverride ?? primary?.reflection,
                        })}
                      </WriterNoteHtml>
                    )}
                  </SyncedField>
                  <CustomSectionSlot
                    {...customSectionProps('conc-reflection')}
                  />

                  <SubHeading>
                    {t('report.builder.preview.next_steps')}
                  </SubHeading>
                  <SyncedField>
                    {reportInclusions.nextSteps === false ? (
                      <p
                        style={{
                          margin: 0,
                          color: '#9ca3af',
                          fontStyle: 'italic',
                        }}
                      >
                        {t('report.builder.preview.text_available')}
                      </p>
                    ) : (
                      <WriterNoteHtml
                        key={`conc-nextsteps-${primary?.id}-${nextStepsVersion}`}
                        emptyPlaceholder={t(
                          'report.builder.preview.no_text_entered'
                        )}
                        onBlur={async (text) => {
                          if (!primary?.id) return;
                          try {
                            await axios.put(
                              `/project/${primary.id}/next-steps`,
                              { nextSteps: text }
                            );
                            setNextStepsOverride(text);
                            const ch = new BroadcastChannel('takso_activity');
                            ch.postMessage({
                              type: 'nextSteps',
                              activityId: primary.id,
                              nextSteps: text,
                            });
                            ch.close();
                          } catch (e) {
                            console.error('Failed to save next steps', e);
                          }
                        }}
                      >
                        {HtmlService.convertToInnerHtml({
                          children: nextStepsOverride ?? primary?.nextSteps,
                        })}
                      </WriterNoteHtml>
                    )}
                  </SyncedField>
                  <CustomSectionSlot
                    {...customSectionProps('conc-nextsteps-notes')}
                  />
                </CollapsibleSection>
              )}

              {/* ── Appendices ── */}
              {SECTION_VISIBLE['Appendices'] !== false && (
                <CollapsibleSection
                  title={t('report.builder.preferences.appendices')}
                  anchorId="sec-appendices"
                >
                  <SubHeading>
                    {t('report.builder.preview.appendix')}{' '}
                    {appendixNum.evidence}: {t('common.evidence')}
                  </SubHeading>
                  {(() => {
                    if (!objectives?.length) {
                      return (
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: REPORT_FONT.note,
                            fontStyle: 'italic',
                            fontFamily: 'Arial, sans-serif',
                            ...PH,
                            borderLeft: '2px solid #f3f4f6',
                            paddingLeft: 8,
                          }}
                        >
                          [{t('report.builder.preview.evidence.no_items')}]
                        </div>
                      );
                    }
                    const seen = new Map();
                    const evIndex = [];
                    for (const obj of objectives) {
                      const title = obj.outcomeTitle || '';
                      for (const ev of obj.evidence || []) {
                        const c = (ev.content || '').trim();
                        if (!c) continue;
                        if (seen.has(c)) {
                          const item = seen.get(c);
                          if (title && !item.outcomes.includes(title))
                            item.outcomes.push(title);
                        } else {
                          const item = {
                            type: ev.type || '',
                            source: ev.source || '',
                            content: c,
                            outcomes: title ? [title] : [],
                          };
                          seen.set(c, item);
                          evIndex.push(item);
                        }
                      }
                    }
                    if (!evIndex.length) {
                      return (
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: REPORT_FONT.note,
                            fontStyle: 'italic',
                            fontFamily: 'Arial, sans-serif',
                            ...PH,
                            borderLeft: '2px solid #f3f4f6',
                            paddingLeft: 8,
                          }}
                        >
                          [{t('report.builder.preview.evidence.no_items')}]
                        </div>
                      );
                    }
                    const NAVY = '#1A2B5E';
                    const measLabel = evIndex.some(
                      (item) => item.outcomes.length > 1
                    )
                      ? t('report.builder.preview.measures')
                      : t('report.builder.preview.measure');
                    const thStyle = {
                      background: NAVY,
                      color: '#fff',
                      padding: '5px 8px',
                      textAlign: 'left',
                      verticalAlign: 'top',
                      fontWeight: 700,
                      fontSize: REPORT_FONT.h2,
                    };
                    return (
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: REPORT_FONT.body,
                          fontFamily: 'Arial, sans-serif',
                          marginTop: 6,
                          tableLayout: 'fixed',
                        }}
                      >
                        <colgroup>
                          <col style={{ width: '20%' }} />
                          <col style={{ width: '42%' }} />
                          <col style={{ width: '38%' }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th style={thStyle}>{t('common.type')}</th>
                            <th style={thStyle}>
                              {t('activity.evaluation.source')}
                            </th>
                            <th style={thStyle}>{measLabel}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {evIndex.flatMap((item, ei) => [
                            <tr
                              key={`d${ei}`}
                              style={{
                                background: showDataTint
                                  ? '#f0fdf4'
                                  : 'transparent',
                              }}
                            >
                              <td
                                style={{
                                  padding: '5px 8px',
                                  verticalAlign: 'top',
                                  borderTop: '1px solid #d1d5db',
                                  borderLeft: '1px solid #d1d5db',
                                  borderRight: '1px solid #d1d5db',
                                }}
                              >
                                {item.type || (
                                  <em style={{ color: '#9ca3af' }}>
                                    [{t('common.type').toLowerCase()}]
                                  </em>
                                )}
                              </td>
                              <td
                                style={{
                                  padding: '5px 8px',
                                  verticalAlign: 'top',
                                  borderTop: '1px solid #d1d5db',
                                  borderRight: '1px solid #d1d5db',
                                  fontStyle: 'italic',
                                  color: '#555',
                                  fontSize: REPORT_FONT.note,
                                  overflowWrap: 'anywhere',
                                }}
                              >
                                {item.source || (
                                  <em style={{ color: '#9ca3af' }}>
                                    [
                                    {t(
                                      'activity.evaluation.source'
                                    ).toLowerCase()}
                                    ]
                                  </em>
                                )}
                              </td>
                              <td
                                style={{
                                  padding: '5px 8px',
                                  verticalAlign: 'top',
                                  borderTop: '1px solid #d1d5db',
                                  borderRight: '1px solid #d1d5db',
                                }}
                              >
                                {item.outcomes.length ? (
                                  item.outcomes.map((t, oi) => (
                                    <div key={oi} style={{ lineHeight: 1.5 }}>
                                      • {t}
                                    </div>
                                  ))
                                ) : (
                                  <em style={{ color: '#9ca3af' }}>
                                    [
                                    {t(
                                      'report.builder.preview.no_outcome_linked'
                                    )}
                                    ]
                                  </em>
                                )}
                              </td>
                            </tr>,
                            <tr key={`c${ei}`}>
                              <td
                                colSpan={3}
                                style={{
                                  padding: '5px 10px',
                                  fontStyle: 'italic',
                                  color: '#374151',
                                  borderLeft: '1px solid #d1d5db',
                                  borderRight: '1px solid #d1d5db',
                                  borderBottom: '1px solid #d1d5db',
                                  lineHeight: 1.5,
                                }}
                              >
                                {item.content}
                              </td>
                            </tr>,
                          ])}
                        </tbody>
                      </table>
                    );
                  })()}

                  {showFinancialResult && (
                    <>
                      {/* heading + table are never split across pages */}
                      <div data-keep-together="financial-result">
                        <SubHeading>
                          {t('report.builder.preview.appendix')}{' '}
                          {appendixNum.financial}:{' '}
                          {t(
                            'activity.documentation.financialSummary.financial_result'
                          )}
                        </SubHeading>
                        {/* from the activity record, not editable here — green
                            "non-editable" tint like the other DataPanels */}
                        <DataPanel tight>
                          <FinancialResultTable summary={financialSummary} />
                        </DataPanel>
                      </div>
                      {reportInclusions.financialNote !== false && (
                        <SyncedField style={{ marginTop: 10 }}>
                          <WriterNoteHtml
                            key={`financial-note-${primary?.id}-${financialNoteVersion}`}
                            emptyPlaceholder={t(
                              'report.builder.preview.no_text_entered'
                            )}
                            onBlur={async (html) => {
                              if (!primary?.id) {
                                return;
                              }
                              try {
                                await axios.put(`/project/${primary.id}`, {
                                  data: { financialNote: html },
                                });
                                setFinancialNoteOverride(html);
                                const ch = new BroadcastChannel(
                                  'takso_activity'
                                );
                                ch.postMessage({
                                  type: 'financialNote',
                                  activityId: primary.id,
                                  financialNote: html,
                                });
                                ch.close();
                              } catch (e) {
                                console.error(
                                  'Failed to save financial note',
                                  e
                                );
                              }
                            }}
                          >
                            {HtmlService.convertToInnerHtml({
                              children:
                                financialNoteOverride ?? primary?.financialNote,
                            })}
                          </WriterNoteHtml>
                        </SyncedField>
                      )}
                    </>
                  )}

                  {hasSurveyResponses && (
                    <>
                      <SubHeading>
                        {t('report.builder.preview.appendix')}{' '}
                        {appendixNum.survey}:{' '}
                        {t('report.builder.preview.survey_responses')}
                      </SubHeading>
                      {(() => {
                        const sessions = surveyResponseComments?.sessions || [];
                        const placeholderStyle = {
                          marginTop: 4,
                          fontSize: REPORT_FONT.note,
                          fontStyle: 'italic',
                          fontFamily: 'Arial, sans-serif',
                          ...PH,
                          borderLeft: '2px solid #f3f4f6',
                          paddingLeft: 8,
                        };

                        // Toggle off: tease that comments exist rather than
                        // silently omitting the appendix, so the author knows
                        // to turn it on if they want them. (hasSurveyResponses
                        // being true here guarantees real data exists — the
                        // "no comments" placeholder is handled by omitting this
                        // whole block above, not inside it.)
                        if (!showSurveyResponsesAppendix) {
                          return (
                            <div style={placeholderStyle}>
                              [
                              {t(
                                'report.builder.preview.survey_responses_appendix_hint'
                              )}
                              ]
                            </div>
                          );
                        }

                        // Colours/padding/font-size for these cells live in
                        // .rb-survey-table (ReportBuilder.scss), not inline —
                        // the global `table th`/`table td` reset is !important,
                        // so inline styles here would be silently discarded.
                        // Only the properties that reset doesn't touch
                        // (text-align, vertical-align) stay inline.
                        return sessions.map((session) => (
                          <div key={session.planId} style={{ marginTop: 14 }}>
                            <div
                              style={{
                                fontSize: REPORT_FONT.h3,
                                fontWeight: REPORT_WEIGHT.h3,
                                color: '#111827',
                              }}
                            >
                              {session.planName}
                            </div>
                            <div
                              style={{
                                fontSize: REPORT_FONT.note,
                                color: '#374151',
                                marginTop: 2,
                              }}
                            >
                              {t('common.method')}: {session.methodName}
                            </div>
                            <div
                              style={{
                                fontSize: REPORT_FONT.note,
                                fontStyle: 'italic',
                                color: '#6b7280',
                                marginTop: 1,
                                marginBottom: 6,
                              }}
                            >
                              {t('report.builder.preview.ratings_from_1_to_10')}
                            </div>
                            <table
                              className="rb-survey-table"
                              style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: REPORT_FONT.body,
                                fontFamily: 'Arial, sans-serif',
                                tableLayout: 'fixed',
                              }}
                            >
                              {(() => {
                                // After/Before stay narrow (numbers only); the
                                // rest of each outcome's share goes to Comment —
                                // without this, tableLayout:fixed with no
                                // colgroup splits all 3×N columns evenly,
                                // squeezing comments into unreadably narrow,
                                // deeply-wrapped cells.
                                const n = session.outcomes.length || 1;
                                // Content is never more than "10" (2 chars), and
                                // the sub-header is now 8px — this only needs to
                                // be wide enough for "Before" at that size, not
                                // the old 10px/14px-rendered version. Whatever
                                // isn't used here goes to Comment.
                                const numColPct = Math.min(7, 20 / n);
                                const commentColPct = 100 / n - numColPct * 2;
                                return (
                                  <colgroup>
                                    {session.outcomes.flatMap((oc) => [
                                      <col
                                        key={`${oc.outcomeId}-a`}
                                        style={{ width: `${numColPct}%` }}
                                      />,
                                      <col
                                        key={`${oc.outcomeId}-b`}
                                        style={{ width: `${numColPct}%` }}
                                      />,
                                      <col
                                        key={`${oc.outcomeId}-c`}
                                        style={{ width: `${commentColPct}%` }}
                                      />,
                                    ])}
                                  </colgroup>
                                );
                              })()}
                              <thead>
                                <tr>
                                  {session.outcomes.map((oc) => (
                                    <th
                                      key={oc.outcomeId}
                                      colSpan={3}
                                      className="rb-survey-table__group-th"
                                      style={{ textAlign: 'left' }}
                                    >
                                      {oc.shortLabel || oc.title}
                                    </th>
                                  ))}
                                </tr>
                                <tr>
                                  {session.outcomes.map((oc) => (
                                    <React.Fragment key={oc.outcomeId}>
                                      <th
                                        className="rb-survey-table__sub-th"
                                        style={{ textAlign: 'center' }}
                                      >
                                        {t('activity.evaluation.after')}
                                      </th>
                                      <th
                                        className="rb-survey-table__sub-th"
                                        style={{ textAlign: 'center' }}
                                      >
                                        {t('activity.evaluation.before')}
                                      </th>
                                      <th
                                        className="rb-survey-table__sub-th"
                                        style={{ textAlign: 'center' }}
                                      >
                                        {t('common.comment')}
                                      </th>
                                    </React.Fragment>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {session.rows.map((row, ri) => (
                                  <tr
                                    key={row.responseFormId ?? ri}
                                    style={{
                                      background:
                                        ri % 2 === 1
                                          ? '#f9fafb'
                                          : 'transparent',
                                    }}
                                  >
                                    {session.outcomes.map((oc) => {
                                      const cell = row.byOutcome[oc.outcomeId];
                                      return (
                                        <React.Fragment key={oc.outcomeId}>
                                          <td
                                            className="rb-survey-table__num-td"
                                            style={{
                                              textAlign: 'center',
                                              verticalAlign: 'top',
                                            }}
                                          >
                                            {cell?.after ?? ''}
                                          </td>
                                          <td
                                            className="rb-survey-table__num-td rb-survey-table__num-td--before"
                                            style={{
                                              textAlign: 'center',
                                              verticalAlign: 'top',
                                            }}
                                          >
                                            {/* Post-only outcomes (sOutcome.prePost = 0,
                                            e.g. "Creativity stimulated") never had a
                                            "before" rating to collect — a dash makes
                                            that explicit instead of reading as a gap
                                            in the data. */}
                                            {oc.isPrePost
                                              ? (cell?.before ?? '')
                                              : '-'}
                                          </td>
                                          <td
                                            className="rb-survey-table__comment-td"
                                            style={{ verticalAlign: 'top' }}
                                          >
                                            {cell?.comment || (
                                              <em style={{ color: '#9ca3af' }}>
                                                [
                                                {t(
                                                  'report.builder.preview.no_comment_for_this_measure'
                                                )}
                                                ]
                                              </em>
                                            )}
                                          </td>
                                        </React.Fragment>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ));
                      })()}
                    </>
                  )}

                  {totalContractors > 0 && (
                    <>
                      <SubHeading>
                        {t('report.builder.preview.appendix')}{' '}
                        {appendixNum.contractors}: {t('common.contractors')}
                      </SubHeading>
                      {reportInclusions.contractorsTable !== false && (
                        <table
                          style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: REPORT_FONT.body,
                            fontFamily: 'Arial, sans-serif',
                            marginTop: 6,
                          }}
                        >
                          <thead>
                            <tr>
                              <th
                                style={{
                                  textAlign: 'left',
                                  padding: '5px 8px',
                                  fontSize: REPORT_FONT.th,
                                  color: '#6b7280',
                                  textTransform: 'uppercase',
                                  borderBottom: '1px solid #d1d5db',
                                }}
                              >
                                {t('report.count')}
                              </th>
                              <th
                                style={{
                                  textAlign: 'left',
                                  padding: '5px 8px',
                                  fontSize: REPORT_FONT.th,
                                  color: '#6b7280',
                                  textTransform: 'uppercase',
                                  borderBottom: '1px solid #d1d5db',
                                }}
                              >
                                {t(
                                  'report.builder.contractors.occupation_category'
                                )}
                              </th>
                              <th
                                style={{
                                  textAlign: 'left',
                                  padding: '5px 8px',
                                  fontSize: REPORT_FONT.th,
                                  color: '#6b7280',
                                  textTransform: 'uppercase',
                                  borderBottom: '1px solid #d1d5db',
                                }}
                              >
                                {t('report.builder.contractors.service_type')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {flattenOccupationRows(contractorCategories).map(
                              (r, i) => (
                                <tr
                                  key={r.key}
                                  style={{
                                    background:
                                      i % 2 === 1 ? '#f9fafb' : 'transparent',
                                  }}
                                >
                                  <td style={{ padding: '5px 8px' }}>
                                    {r.count}
                                  </td>
                                  <td style={{ padding: '5px 8px' }}>
                                    {r.categoryLabel}
                                  </td>
                                  <td style={{ padding: '5px 8px' }}>
                                    {r.serviceType}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      )}
                      {reportInclusions.contractorsMap !== false && (
                        <div style={{ marginTop: 14 }}>
                          <ContractorMap
                            activityIds={[primary?.id]}
                            showReportToggle={false}
                          />
                        </div>
                      )}
                    </>
                  )}
                </CollapsibleSection>
              )}

              {/* ── Glossary ── */}
              {SECTION_VISIBLE['Glossary'] !== false && (
                <CollapsibleSection
                  title={t('report.builder.preferences.glossary')}
                  anchorId="sec-glossary"
                >
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: REPORT_FONT.note,
                      fontStyle: 'italic',
                      color: '#9ca3af',
                    }}
                  >
                    [{t('report.builder.preview.glossary_to_be_completed')}]
                  </div>
                </CollapsibleSection>
              )}
            </ReportTintContext.Provider>

            {/* ── End signature ── */}
            {prefs.endSignatureUrl && (sections.endSignature ?? true) && (
              <div
                style={{
                  marginTop: 40,
                  paddingTop: 24,
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <img
                  src={prefs.endSignatureUrl}
                  alt="Organisation signature"
                  style={{ maxWidth: '100%', maxHeight: 200 }}
                />
              </div>
            )}
          </div>
          {/* end padding wrapper */}

          {/* Footer bar */}
          {showFooter && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 56px',
                borderTop: '1px solid #e5e7eb',
                fontSize: REPORT_FONT.note,
                color: '#6b7280',
              }}
            >
              <span>
                {footer.centreText || t('report.builder.prepared_using_takso')}
              </span>
              {footer.showPageNumbers !== false && <span>1</span>}
            </div>
          )}
        </div>
      </div>{' '}
      {/* end A4 maxWidth wrapper */}
      <p
        style={{
          fontSize: 12,
          color: '#9ca3af',
          margin: 0,
          fontStyle: 'italic',
        }}
      >
        {t('report.builder.preview.contents_page_hint')}
      </p>
      {/* Image picker modal — global, used for cover page and future image slots */}
      <ImagePickerModal
        open={showImagePicker}
        projectId={primary?.id}
        multiple={imagePickerMultiple}
        onSelect={handleImagePickerSelect}
        onClose={() => {
          setShowImagePicker(false);
          setImagePickerSlot(null);
          setImagePickerMultiple(false);
        }}
      />
      {/* Custom-section choice modal — shared across every break's
          "Add custom section (optional)" button. */}
      <CustomSectionModal
        open={!!customSectionModalSlot}
        onCancel={() => setCustomSectionModalSlot(null)}
        onAdd={handleAddCustomSection}
      />
      {/* Cover image crop modal — opens on a freshly-picked image
          (pendingCoverImage) or to reposition/re-crop the existing one. */}
      <CoverImageCropModal
        open={showCoverCropModal}
        imageUrl={pendingCoverImage?.url || coverImg?.url}
        initialImage={coverCropInitialImage}
        initialCrop={coverCropInitialCrop}
        onCancel={handleCoverCropCancel}
        onSave={handleCoverCropSave}
      />
    </div>
  );
};

export default ReportPreview;
